/**
 * TREMEC Demo Translations — EN + ES
 * All keys used across the 5 demo views
 */

export type Language = "en" | "es";

export type TranslationDictionary = Record<string, string>;

export const translations: Record<Language, TranslationDictionary> = {
  en: {
    // =========================================================================
    // DEMO BANNER
    // =========================================================================
    "tremec.banner.text": "Demo Mode — Showing representative data for TREMEC's supply chain in QRO. Connect your ERP for live data.",
    "tremec.banner.reset": "Reset Demo",

    // =========================================================================
    // COMMON
    // =========================================================================
    "common.ready": "Ready",
    "common.pending": "Pending",
    "common.verified": "Verified",
    "common.originating": "Originating",
    "common.ytd": "YTD",
    "common.pass": "Pass",
    "common.borderline": "Borderline",
    "common.above_threshold": "Above 75% threshold",
    "common.at_threshold": "At threshold — monitor",
    "common.flagged": "FLAGGED",
    "common.supplier": "Supplier",
    "common.product": "Product",
    "common.steel": "Steel",
    "common.status": "Status",

    // =========================================================================
    // SUPPLIER VIEW
    // =========================================================================
    "supplier.tab_quote": "Quote",
    "supplier.tab_invoice": "Invoice",

    // =========================================================================
    // QUOTE FLOW
    // =========================================================================
    "quote.check_sat": "SAT RFC active — VIGENTE",
    "quote.check_repse": "REPSE registration valid",
    "quote.check_steel": "Steel source: Ternium MX ✓",
    "quote.check_rvc": "RVC estimate: 77.1% (threshold 75%)",
    "quote.check_steel_pct": "Steel originating: 84.3% (threshold 70%)",
    "quote.check_hs": "HS 8483.40 — USMCA eligible",
    "quote.rfq_title": "INCOMING RFQ — TREMEC",
    "quote.rfq_number": "RFQ #",
    "quote.product": "Product",
    "quote.product_name": "Synchronizer Hub — SAE 8620",
    "quote.qty": "Qty",
    "quote.qty_value": "2,400 pcs",
    "quote.drawing": "Drawing",
    "quote.program": "Program",
    "quote.submit": "Submit Quote to TREMEC",
    "quote.prequalifying": "PRE-QUALIFYING...",
    "quote.prequalified": "PRE-QUALIFIED — USMCA Ready",
    "quote.unit_price": "Unit Price",
    "quote.total": "Total",
    "quote.est_rvc": "Est. RVC",
    "quote.prequalified_time": "Pre-qualified in 14 seconds",

    // =========================================================================
    // QUOTE STEP 2 — AI BUILD ANIMATION
    // =========================================================================
    "quote.ai_building": "AI BUILDING QUOTE...",
    "quote.ai_building_sub": "Pulling from 847 TREMEC POs, SAT profile, capacity data",
    "quote.field_unit_price": "Unit Price",
    "quote.field_unit_price_value": "MXN $597.55",
    "quote.field_unit_price_source": "AI — 847 POs with TREMEC",
    "quote.field_lead_time": "Lead Time",
    "quote.field_lead_time_value": "28 days",
    "quote.field_lead_time_source": "AI — current capacity 74%",
    "quote.field_steel": "Steel Source",
    "quote.field_steel_value": "Ternium México — Monterrey",
    "quote.field_steel_source": "Profile — SAT verified",
    "quote.field_rvc": "RVC Calculation",
    "quote.field_rvc_value": "76.2%",
    "quote.field_rvc_source": "AI — Net Cost Method",
    "quote.field_cost": "Cost Breakdown",
    "quote.field_cost_value": "Material $187 / Forging $205 / Machining $113 / HT $39",
    "quote.field_cost_source": "Profile",
    "quote.field_iatf": "IATF Certification",
    "quote.field_iatf_value": "IATF 16949 valid 2027-08",
    "quote.field_iatf_source": "Profile — on file",
    "quote.field_cert": "Cert Readiness",
    "quote.field_cert_value": "Generated in 28 seconds",
    "quote.field_cert_source": "AI — Auto",
    "quote.field_payment": "Payment Terms",
    "quote.field_payment_value": "NET 90",
    "quote.field_payment_source": "EDI — TREMEC standard",
    "quote.steel_origin_badge": "Steel origin verified: Ternium Monterrey, 84.3% originating — clears 70% threshold",
    "quote.rvc_badge": "RVC 76.2% — clears 75% core part threshold",
    "quote.submit_to_tremec": "Submit Quote to TREMEC →",

    // =========================================================================
    // QUOTE STEP 3 — TREMEC FEEDBACK
    // =========================================================================
    "quote.tremec_reviewing": "TREMEC reviewing quote...",
    "quote.waiting_purchaser_review": "Waiting for TREMEC to review on Purchaser view...",
    "quote.feedback_received": "TREMEC FEEDBACK RECEIVED",
    "quote.feedback_from": "Carlos Mendez — Purchasing Manager",
    "quote.draft_responses": "Draft AI Responses →",

    // =========================================================================
    // QUOTE STEP 4 — AI FEEDBACK RESPONSE
    // =========================================================================
    "quote.ai_responding": "AI DRAFTING RESPONSES...",
    "quote.response_traceability_title": "Steel Heat Lot Traceability",
    "quote.response_traceability": "3 Ternium heat lots on file: TERN-MTY-2026-0892, TERN-MTY-2025-1847, TERN-MTY-2025-1203. Full chain: Ternium MTY → FMB forge+machine+HT → TREMEC assembly. All certs current.",
    "quote.response_traceability_source": "Profile — verified",
    "quote.response_payment_title": "NET 60 Analysis",
    "quote.response_payment": "NAFIN factoring bridges the MXN $385K gap at 2.5% discount. NET 60 accepted — Poniente AI confirms cash flow remains positive with factoring.",
    "quote.response_payment_source": "AI — Poniente analysis",
    "quote.approve_send": "Approve & Send to TREMEC →",

    // =========================================================================
    // INVOICE FLOW
    // =========================================================================
    "invoice.upload_title": "Upload CFDI Invoice",
    "invoice.upload_subtitle": "XML file from your accounting system",
    "invoice.upload_btn": "Upload CFDI XML",
    "invoice.validated": "CFDI Validated — All Fields Matched",

    // =========================================================================
    // NINE-POINT FORM
    // =========================================================================
    "nine_point.title": "9-Point Verification Form",
    "nine_point.pt1_label": "Seller Name & Address",
    "nine_point.pt2_label": "Seller Tax ID",
    "nine_point.pt2_value": "RFC: FMB120523LK7 — SAT: VIGENTE",
    "nine_point.pt3_label": "Buyer Name & Address",
    "nine_point.pt4_label": "Description of Goods",
    "nine_point.pt4_value": "Maza de sincronizador forjada SAE 8620 CrMo — 2,400 pcs — HS 8483.40.50",
    "nine_point.pt5_label": "HS Code & RVC",
    "nine_point.pt5_value": "HS 8483.40.50 — RVC 76.2% (Net Cost) — Threshold 75%",
    "nine_point.pt6_label": "Blanket Period",
    "nine_point.pt7_label": "Goods Description",
    "nine_point.pt8_label": "Net Cost RVC Calculation",
    "nine_point.pt8_value": "Material MXN $187.40 + Forging $205.00 + Machining $112.80 + HT $38.50 — Non-orig: $53.85 — RVC: 76.2%",
    "nine_point.source_profile": "Profile",
    "nine_point.source_edi": "EDI",
    "nine_point.source_cfdi": "CFDI",
    "nine_point.source_ai": "AI",
    "nine_point.source_you": "You",
    "nine_point.auto_filled": "8/9 auto-filled",
    "nine_point.pt9_label": "Authorized Signature",
    "nine_point.pt9_pending": "Signature required",
    "nine_point.legend": "Profile | EDI | CFDI | AI | You",
    "nine_point.sign_btn": "Sign Certificate →",

    // =========================================================================
    // SIGNATURE
    // =========================================================================
    "signature.clear": "Clear",
    "signature.confirm": "Confirm Signature",

    // =========================================================================
    // COMPLETE
    // =========================================================================
    "complete.title": "Certificate Complete",
    "complete.verified": "9-point form verified & sealed",
    "complete.time": "32 sec",
    "complete.sealed": "SHA-256 sealed",
    "complete.sent": "Sent to TREMEC",

    // =========================================================================
    // PURCHASER VIEW (TREMEC)
    // =========================================================================
    "tremec.title": "TREMEC Compliance Command Center",
    "tremec.live": "LIVE",
    "tremec.corridor": "QRO ↔ Detroit Corridor — 2026 Joint Review",
    "tremec.review_score": "Review Score",
    "tremec.stat_shipments": "Shipments",
    "tremec.stat_verified": "USMCA Verified",
    "tremec.stat_target": "Target: 95%",
    "tremec.stat_cert_time": "Avg Cert Time",
    "tremec.stat_vs_manual": "vs 4.2 hrs manual",
    "tremec.stat_review": "2026 Review Score",
    "tremec.activity_feed": "Supplier Activity Feed",
    "tremec.event_quote": "QUOTE",
    "tremec.event_quote_received": "Quote Received",
    "tremec.event_quote_detail": "2,400 Synchronizer Hubs — SAE 8620 — MXN $1,663,579 — RVC estimate 77.1%",
    "tremec.award_po": "Award PO",
    "tremec.event_cfdi": "CFDI Uploaded",
    "tremec.event_cfdi_detail": "CFDI 4.0 validated — Batch #4456 — MXN $1,663,579 — RFC matched",
    "tremec.event_signed": "Certificate Signed",
    "tremec.event_signed_detail": "9-point form complete — RVC 76.2% verified — Steel: Ternium MX",
    "tremec.flagged_title": "RVC 68.4% — Below 75% Threshold",
    "tremec.flagged_detail": "Gear Blanks SAE 4140 — Steel origin UNVERIFIED (Aceros Nacionales) — Price 41% below LME",
    "tremec.flagged_action": "Action: Request mill certificate — Cannot ship duty-free without verified origin",
    "tremec.recent_verifications": "Recent Verifications — All Suppliers",

    // Purchaser View — new enhanced events
    "tremec.compliance_steel": "Steel: Ternium Monterrey — 84.3% originating (70% threshold)",
    "tremec.compliance_rvc": "RVC: 76.2% — clears 75% core part threshold",
    "tremec.compliance_iatf": "IATF 16949 — on file, valid through 2027-08",
    "tremec.event_review": "REVIEW",
    "tremec.event_review_title": "Quote Under Review",
    "tremec.event_review_detail": "Carlos Mendez reviewing FMB quote — 2 comments pending",
    "tremec.send_feedback": "Send Feedback to Supplier",
    "tremec.event_po": "PO AWARDED",
    "tremec.event_po_title": "Purchase Order Awarded — TRE-2026-PO-1847",
    "tremec.event_po_detail": "NET 60 — MXN $1,663,579 — All compliance fields pre-populated from quote",
    "tremec.award_po_btn": "Award PO →",
    "tremec.po_awarded_label": "PO Awarded",
    "tremec.feedback_sent": "Feedback sent to supplier",

    // =========================================================================
    // BANK VIEW
    // =========================================================================
    "bank.title": "Digital Compliance Jacket",
    "bank.batch": "Batch #4456",
    "bank.subtitle": "Forjas del Bajío → TREMEC — Synchronizer Hubs SAE 8620",
    "bank.jacket_ready": "Jacket Ready",
    "bank.signature_pending": "Signature Pending",
    "bank.funding": "Funding Recommendation",
    "bank.not_eligible": "Not eligible — Awaiting 9-point signature from supplier",
    "bank.amount": "Invoice Amount",
    "bank.advance_rate": "Advance Rate",
    "bank.available": "Available",
    "bank.risk_score": "Risk Score",
    "bank.buyer_rating": "Buyer Rating",
    "bank.maturity": "Maturity",
    "bank.days": "days",
    "bank.triple_source": "Triple Source Verification",
    "bank.sat_label": "SAT (Tax Authority)",
    "bank.sat_detail": "RFC: FMB120523LK7 VIGENTE — CFDI UUID matched — Amount: MXN $1,663,579",
    "bank.edi_label": "EDI (856 ASN)",
    "bank.edi_detail": "PO# TRE-2026-PO-1847 — 2,400 units × MXN $597.55 — Matched",
    "bank.portal_label": "Buyer Portal (TREMEC)",
    "bank.portal_detail": "Portal status: APPROVED — Payment date: May 6, 2026 — Amount matched",
    "bank.full_consensus": "FULL CONSENSUS — 3/3 Sources Verified",
    "bank.steel_origin": "Steel Origin & Traceability",
    "bank.steel_requirement": "USMCA Requirement: Steel must be melted & poured in USMCA territory. Originating content ≥ 70%.",
    "bank.steel_grade": "Steel Grade",
    "bank.steel_mill": "Mill",
    "bank.steel_cert": "Mill Cert",
    "bank.steel_lme": "LME Variance",
    "bank.steel_ytd": "YTD",
    "bank.steel_traceability": "Supply chain traceability:",
    "bank.vendor_codes": "Vendor Code Verification",
    "bank.buyer": "Buyer",
    "bank.assigned": "Assigned",
    "bank.pos": "POs",
    "bank.volume": "Volume",
    "bank.vendor_commentary": "11-year TREMEC relationship (847 POs). Kirchhoff added 2019 for buyer diversification. Both codes active and verified via ERP cross-reference.",
    "bank.audit_trail": "Immutable Audit Trail",
    "bank.export_pdf": "Export PDF",
    "bank.export_json": "Export JSON",
    "bank.export_core": "Export CORE",

    // =========================================================================
    // TRADE CONNECT VIEW
    // =========================================================================
    "tc.doc_1": "CFDI 4.0 XML — Validated",
    "tc.doc_2": "9-Point USMCA Certificate",
    "tc.doc_3": "Bill of Materials + RVC Calculation",
    "tc.doc_4": "Triple Source Verification Report",
    "tc.doc_5": "Steel Origin: Ternium MTY Mill Cert",
    "tc.doc_6": "EDI 856 ASN — TREMEC PO Match",
    "tc.doc_7": "Supplier Profile — FMB (82/100)",
    "tc.title": "TradeConnect Sidecar",
    "tc.subtitle": "The complete data package that travels alongside the invoice — from Forjas del Bajío to TREMEC to any financing partner.",
    "tc.sidecar_from": "Forjas del Bajío",
    "tc.sidecar_middle": "alisio sidecar",
    "tc.sidecar_to": "TREMEC / Bank",
    "tc.data_package": "Data Package",
    "tc.exporting": "Exporting...",
    "tc.export_btn": "Export Sidecar Package",
    "tc.export_success": "Sidecar Exported Successfully",
    "tc.pt9_required": "Pt.9 signature required before export",
    "tc.kill_shot": "Every invoice carries its own proof. No phone calls. No PDF attachments. No 'can you resend the certificate.' The data just travels.",

    // =========================================================================
    // SECRETARY VIEW
    // =========================================================================
    "sec.comparison_today": "Today (without Alisio)",
    "sec.comparison_with_alisio": "With Alisio",
    "sec.comp_no_financing": "No receivables financing",
    "sec.comp_nafin_factoring": "NAFIN factoring — same day",
    "sec.comp_informal": "Informal factoring (5-8%)",
    "sec.comp_bancomext": "Bancomext pre-qualified",
    "sec.comp_nafin": "NAFIN rates (2.5%)",
    "sec.comp_discount": "2.5% — better than informal",
    "sec.comp_devbank": "No dev bank access",
    "sec.problem_title": "The Problem",
    "sec.problem_text": "80% of Mexican T1 auto suppliers cannot access institutional financing. They have the orders, the certifications, the verified relationships — but no digital proof that a bank can trust.",
    "sec.company_title": "The Company",
    "sec.founded": "Founded",
    "sec.employees": "Employees",
    "sec.revenue": "Revenue",
    "sec.score": "Alisio Score",
    "sec.roberto_problem": "Roberto Calderón has MXN $4.5M floating on 90-day TREMEC terms. He makes payroll every 15 days (MXN $385K). Without factoring, he borrows informally at 5-8% or delays supplier payments — both of which hurt his score and his growth.",
    "sec.capital_stack": "The Capital Stack",
    "sec.below_level1_title": "No Digital Proof",
    "sec.below_level1_text": "This is where 80% of T1 suppliers sit today. They have TREMEC POs, verified steel, IATF 16949 — but no way to package it for a bank. The result: informal factoring at 5-8%, or simply waiting 90 days.",
    "sec.level1_title": "NAFIN Factoring",
    "sec.level1_status": "QUALIFIED",
    "sec.level1_without": "Without Alisio, Roberto has to manually collect his CFDI, prove his steel origin, coordinate with TREMEC's portal, and hope the bank trusts his documents. With Alisio: Triple Source verification + Digital Compliance Jacket = instant eligibility.",
    "sec.level1_this_invoice": "This invoice qualifies NOW. Roberto can factor it today.",
    "sec.level2_title": "Bancomext PyMEx Credit",
    "sec.level2_status": "WITHIN REACH",
    "sec.level2_gap": "Gap: Needs audited financials. Alisio can auto-generate the financial snapshot from verified transaction history.",
    "sec.level3_title": "Working Capital Line",
    "sec.level3_status": "FUTURE",
    "sec.level3_text": "Requires 18+ months of verified transaction history on Alisio platform.",
    "sec.level4_title": "Equipment Financing",
    "sec.level4_status": "FUTURE",
    "sec.level4_text": "Additional CNC capacity (Mazak / Haas). Requires Level 2 unlocked + equipment appraisal.",
    "sec.level5_title": "Bancomext Term Credit",
    "sec.level5_status": "FUTURE",
    "sec.level5_text": "Requires 3+ years history, audited financials, export documentation. Alisio builds the file automatically over time.",
    "sec.live_proof": "What We Just Proved — Live",
    "sec.live_1": "TREMEC sent an RFQ → Tell Tale classified it in 2 seconds",
    "sec.live_2": "Roberto's 9-point USMCA cert was 8/9 auto-filled from existing data",
    "sec.live_3": "Triple Source verification: SAT + EDI + Portal = FULL consensus",
    "sec.live_4": "Bank jacket generated instantly — MXN $1,414,042 available today",
    "sec.live_conclusion": "Roberto doesn't need a bigger line of credit. He needs proof that his existing business is real. Alisio provides that proof.",
    "sec.policy_title": "Policy Implication",
    "sec.policy_text": "NAFIN and Bancomext already have the programs. The gap is not capital — it's digital infrastructure. Alisio bridges that gap for every supplier in the TREMEC corridor.",
    "sec.policy_impact": "If every T1 supplier in the Querétaro-Detroit corridor had access to Alisio, we estimate MXN $2.4B in unlocked working capital. That's not new money — it's money that's already earned but inaccessible.",
    "sec.sba_title": "SBA Comparison",
    "sec.sba_text": "The U.S. Small Business Administration (SBA) provides loan guarantees for small manufacturers. Mexico has equivalent programs through NAFIN and Bancomext, but adoption is low because the digital proof layer is missing. Alisio fills the same role that automated underwriting platforms fill for SBA lenders in the U.S.",
    "sec.closing": "We are not asking for new policy. We are asking for recognition that the infrastructure to connect existing policy to existing suppliers now exists. Alisio is that infrastructure.",
    "sec.check_rfc_active": "RFC active (FMB120523LK7 — VIGENTE)",
    "sec.check_supplier_gran_empresa": "Supplier to Gran Empresa (TREMEC qualifies)",
    "sec.check_signed_contract": "Signed factoring contract",
    "sec.check_2yr_operating": "2+ years operating (13 years)",
    "sec.check_export_activity": "Export activity (TREMEC → Novi, MI)",
    "sec.check_financial_statements": "Financial statements (has them, not audited)",
    "sec.level1_banks": "BBVA / Banorte / Monex via NAFIN",
    "sec.level2_banks": "Bancomext — Up to $3M USD",
    "sec.level3_amount": "Up to MXN $10M (Commercial bank + NAFIN guarantee)",
    "sec.level5_amount": "Up to MXN $60M, 5-year terms",
    "sec.location": "Circuito El Marqués Norte #47, P.I. El Marqués, QRO",
    "sec.industry": "Closed-die forging + CNC machining + Heat treatment",

    // SECRETARY — VERIFICATION LAYER
    "sec.verification_title": "The Verification Layer",
    "sec.verification_subtitle": "What Alisio verifies — automatically, in real time",
    "sec.verify_sat": "SAT/RFC Status — Active, validated against tax authority",
    "sec.verify_cfdi": "CFDI 4.0 — UUID, amounts, RFC cross-match",
    "sec.verify_steel": "Steel Origin — Mill certificate, melted & poured in USMCA territory",
    "sec.verify_rvc": "RVC Calculation — Net cost method, threshold compliance",
    "sec.verify_edi": "EDI/ASN — Purchase order, quantities, prices matched",
    "sec.verify_portal": "Buyer Portal — Approval status, payment date confirmed",
    "sec.verify_9pt": "9-Point USMCA Form — 8/9 fields auto-filled from verified data",
    "sec.verify_mdv": "Manifestación de Valor — 3-way value reconciliation per sub-supplier",
    "sec.verify_time": "Average verification: 32 seconds — vs 4.2 hours manual",

    // SECRETARY — T1 ↔ T2 COLLABORATION
    "sec.collab_title": "How T1s and T2s Work Together",
    "sec.collab_subtitle": "Alisio sits between tiers — verifying data before it moves up the chain",
    "sec.collab_t2_title": "For the T2 Sub-Supplier",
    "sec.collab_t2_1": "No ERP required — just upload a CFDI",
    "sec.collab_t2_2": "Validated in 30 seconds, not 30 days",
    "sec.collab_t2_3": "Automatic notification if documents need correction",
    "sec.collab_t2_4": "Faster payment — T1 can factor with verified supply chain",
    "sec.collab_t1_title": "For the T1 Supplier",
    "sec.collab_t1_1": "Automated sub-supplier compliance screening",
    "sec.collab_t1_2": "IMMEX protection — bad data blocked before it reaches VUCEM",
    "sec.collab_t1_3": "Audit-ready documentation per sub-supplier",
    "sec.collab_t1_4": "Pre-qualified for financing with a verified chain",
    "sec.collab_example": "In this demo: Alisio validated 3 sub-suppliers for TREMEC's synchronizer program. 2 passed automatically. 1 was blocked — before incorrect data could reach customs.",

    // SECRETARY — REGIONAL ECONOMIC DEVELOPMENT
    "sec.regional_title": "Regional Economic Development",
    "sec.regional_corridor": "The Querétaro-Detroit Corridor",
    "sec.regional_corridor_text": "200+ auto parts manufacturers in Querétaro. Major OEMs: TREMEC, Kirchhoff, Dana, Martinrea. This corridor is the USMCA manufacturing backbone.",
    "sec.regional_multiplier": "The Multiplier Effect",
    "sec.regional_multiplier_text": "When FMB factors a TREMEC invoice, they pay Ternium (steel) faster. Ternium maintains capacity. FMB invests in equipment and hires workers. More payroll means more local spending.",
    "sec.regional_nearshoring": "Nearshoring Readiness",
    "sec.regional_nearshoring_text": "Companies moving production to Mexico need verified supply chains. Alisio provides the digital infrastructure that makes Mexican suppliers bankable and audit-ready for new OEM programs.",
    "sec.regional_formalization": "Supply Chain Formalization",
    "sec.regional_formalization_text": "Every CFDI validated is one more data point in a supplier's credit profile. Over time: more verified data → better credit → cheaper capital → more growth. Alisio brings T2 and T3 suppliers into the formal financial system.",
    "sec.regional_impact": "Estimated MXN $2.4B in unlocked working capital if every T1 supplier in the Querétaro-Detroit corridor had access to Alisio. That is not new money — it is money already earned but currently inaccessible.",

    // =========================================================================
    // DEMO PAGE TABS
    // =========================================================================
    "demo.tab_supplier": "Supplier",
    "demo.tab_purchaser": "Purchaser",
    "demo.tab_bank": "Bank",
    "demo.tab_tradeconnect": "TradeConnect",
    "demo.tab_secretary": "Sec. Economy",
    "demo.tab_receivables": "Receivables",
    "demo.tab_manifestacion": "Manifestación de Valor",
    "demo.reset": "Reset Demo",

    // =========================================================================
    // RECEIVABLES VIEW
    // =========================================================================
    "recv.title": "Accounts Receivable",
    "recv.fmb_perspective": "FMB Perspective",
    "recv.subtitle": "Outstanding invoices, aging, DSO, and factoring eligibility for FMB's buyer portfolio.",
    "recv.stat_outstanding": "Total Outstanding",
    "recv.stat_invoices": "invoices",
    "recv.stat_avg_dso": "Weighted Avg DSO",
    "recv.days": "days",
    "recv.stat_dso_sub": "Weighted by invoice amount",
    "recv.stat_factorable": "Factorable Amount",
    "recv.stat_verified_invoices": "verified invoices",
    "recv.stat_flagged": "Flagged",
    "recv.stat_flagged_sub": "Blocked from factoring",
    "recv.aging_title": "Aging Analysis",
    "recv.current": "Current",
    "recv.table_title": "Invoice Detail",
    "recv.col_invoice": "Invoice",
    "recv.col_buyer": "Buyer",
    "recv.col_description": "Description",
    "recv.col_amount": "Amount",
    "recv.col_terms": "Terms",
    "recv.col_days_left": "Days Left",
    "recv.status_verified": "Verified",
    "recv.status_flagged": "Flagged",
    "recv.status_pending": "Pending",
    "recv.hero_factored": "Hero Invoice Factored",
    "recv.hero_step_sat": "SAT verification — Vigente",
    "recv.hero_step_cfdi": "CFDI timbrado confirmed",
    "recv.hero_step_cert": "USMCA Certificate of Origin signed",
    "recv.hero_step_po": "PO awarded by TREMEC",
    "recv.hero_step_factor": "Factoring available — 85% advance",
    "recv.filter_buyer": "All Buyers",
    "recv.filter_label": "Filter by Buyer",
    "recv.days_to_due": "Days to due",
    "recv.concentration_title": "Buyer Concentration",
    "recv.concentration_note": "Three-buyer portfolio reduces concentration risk. TREMEC at 62% (down from 78%). Katcon and Kirchhoff diversify receivables and improve lending terms.",
    "recv.inv_hero": "Synchronizer Hub — Batch #4456 (SAE 8620)",
    "recv.inv_celaya": "Gear Blanks — Celaya Sub-Supplier (FLAGGED: no mill cert)",
    "recv.inv_aceros": "Forged Ring Gears — Aceros del Bajío",
    "recv.inv_troquelados": "Stamped Synchro Sleeves — Troquelados Irapuato",
    "recv.inv_cnc": "CNC Machined Hubs — Complementos Maq. del Bajío",
    "recv.inv_componentes": "Heat-Treated Couplings — Componentes Auto del Centro",
    "recv.inv_metalicos": "Precision Shafts — Metálicos Industriales Salamanca",
    "recv.inv_kirchhoff": "Structural Brackets — Kirchhoff PO #K-2241",
    "recv.inv_katcon": "Forged Exhaust Flanges — Katcon SCR Euro 7 Program",

    // =========================================================================
    // DEMO PAGE TABS — KATCON
    // =========================================================================
    "demo.tab_katcon_buyer": "Katcon Buyer",
    "demo.tab_katcon_passport": "Digital Passport",

    // =========================================================================
    // KATCON BUYER VIEW
    // =========================================================================
    "katcon.buyer_title": "Supplier Compliance — FMB",
    "katcon.buyer_subtitle": "Katcon's view of FMB compliance for exhaust flange program. TREMEC data not visible.",
    "katcon.supplier_qualified": "Supplier Qualified",
    "katcon.current_po": "Current PO Value",
    "katcon.units": "units",
    "katcon.threshold": "Threshold",
    "katcon.sat_status": "SAT Status",
    "katcon.eu_dpp": "EU DPP Export",
    "katcon.eu_dpp_sub": "Passport + Alisio ready",
    "katcon.rvc_title": "USMCA Regional Value Content",
    "katcon.part_category": "Part Category",
    "katcon.complementary_part": "Complementary Part",
    "katcon.method": "Method",
    "katcon.net_cost": "Net Cost",
    "katcon.originating_materials": "Originating Materials",
    "katcon.vnm_per_unit": "VNM / Unit",
    "katcon.margin": "Margin Above Threshold",
    "katcon.rvc_note": "Complementary part — 65% Net Cost threshold applies per USMCA Annex 4-B Table A.2. Comfortable 16.4 point margin.",
    "katcon.steel_origin": "Steel Origin Verification",
    "katcon.mill": "Mill",
    "katcon.mill_cert": "Mill Cert",
    "katcon.heat_lot": "Heat Lot",
    "katcon.melted_poured": "Melted & Poured",
    "katcon.compliant": "Compliant",
    "katcon.sat_verification": "SAT / CFDI Verification",
    "katcon.issuer": "Issuer",
    "katcon.receiver": "Receiver",
    "katcon.clean": "Clean",
    "katcon.sub_supplier_chain": "Sub-Supplier Compliance Chain",
    "katcon.material_input": "Material / Input",
    "katcon.passport_overlay_title": "Digital Passport + Alisio",
    "katcon.passport_overlay_text": "Your passport tracks internal traceability. Alisio adds: USMCA audit readiness, sub-supplier verification, bankability. Switch to the Digital Passport tab to see the full overlay.",

    // =========================================================================
    // KATCON DIGITAL PASSPORT VIEW
    // =========================================================================
    "passport.title": "Digital Product Passport",
    "passport.subtitle": "Katcon internal passport data (SAP S/4HANA) with Alisio compliance overlay",
    "passport.internal_label": "Katcon Internal Passport",
    "passport.alisio_overlay": "Alisio Verification",
    "passport.overlay_sub": "What the passport does NOT do",
    "passport.compliance_score": "Overall Compliance Score",
    "passport.material_origin": "Material Origin & Composition",
    "passport.grade": "Grade",
    "passport.supplier": "Supplier",
    "passport.heat_lot": "Heat Lot",
    "passport.composition": "Composition",
    "passport.weight": "Weight / Unit",
    "passport.manufacturing": "Manufacturing Process",
    "passport.process": "Process",
    "passport.forge_temp": "Forge Temp",
    "passport.forge_pressure": "Forge Pressure",
    "passport.die_number": "Die Number",
    "passport.heat_treatment": "Heat Treatment",
    "passport.quality": "Quality Records",
    "passport.dimensional": "Dimensional",
    "passport.hardness": "Hardness",
    "passport.surface": "Surface Finish",
    "passport.carbon": "Carbon Footprint",
    "passport.rvc_verification": "RVC Calculation (Alisio)",
    "passport.rvc_passport_note": "Passport tracks material origins but does not calculate USMCA RVC. Alisio classifies originating vs non-originating materials and applies the correct threshold per HS code.",
    "passport.steel_verification": "Steel Melt-and-Pour (Alisio)",
    "passport.lme_deviation": "LME Price Deviation",
    "passport.steel_passport_note": "Passport records steel supplier. Alisio verifies melt-and-pour origin against USMCA Article 4.6 and flags LME price anomalies.",
    "passport.sat_cross_ref": "SAT Cross-Reference (Alisio)",
    "passport.triple_source": "Triple Source Verification",
    "passport.total_sub_suppliers": "Total Sub-Suppliers",
    "passport.blocked": "Blocked",
    "passport.sub_supplier_note": "Passport tracks materials inside Katcon. Alisio extends verification down to Katcon's Tier 2 suppliers — RFC check, EFOS screening, mill certificate verification.",
    "passport.eu_dpp_ready": "EU DPP Export Ready",
    "passport.eu_dpp_note": "Passport data + Alisio verification exportable to EU Digital Product Passport schema. Ready for Katcon's European operations when EU DPP mandate takes effect.",
    "passport.carbon_raw_material": "Raw Material",
    "passport.carbon_forging": "Forging",
    "passport.carbon_machining": "Machining",
    "passport.carbon_heat_treatment": "Heat Treatment",
    "passport.carbon_transport": "Transport",

    // =========================================================================
    // BANK VIEW — DIVERSIFICATION
    // =========================================================================
    "bank.diversification_title": "Buyer Diversification",
    "bank.diversification_note": "Three-buyer portfolio reduces concentration risk. TREMEC at 62% (down from 78%). Lending terms improve with diversified receivables.",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — PAGE
    // =========================================================================
    "mdv.page_title": "Manifestación de Valor",
    "mdv.page_subtitle": "New Mexican customs provision — value reconciliation across the supply chain",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — SECTION A: RECONCILIATION ENGINE
    // =========================================================================
    "mdv.reconciliation_title": "A. Automatic Reconciliation Engine",
    "mdv.reconciliation_subtitle": "3-way comparison: Declared Customs Value vs Commercial Invoice vs Actual Payment",
    "mdv.reconciliation_btn": "Run Reconciliation",
    "mdv.reconciliation_running": "Reconciling...",
    "mdv.reconciliation_complete": "Reconciliation Complete",
    "mdv.col_sub_supplier": "Sub-Supplier",
    "mdv.col_customs_value": "Customs Value",
    "mdv.col_invoice": "Invoice",
    "mdv.col_payment": "Payment",
    "mdv.col_delta": "Delta",
    "mdv.col_status": "Status",
    "mdv.status_flagged": "FLAGGED",
    "mdv.status_clean": "CLEAN PASS",
    "mdv.flagged_alert_title": "Fundición Industrial Celaya — FLAGGED REJECTION",
    "mdv.flagged_alert_detail": "16% value discrepancy detected between declared customs value ($483,840) and commercial invoice ($561,254). This sub-supplier cannot be included in the T-MEC Certificate of Origin until discrepancies are resolved.",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — SECTION B: QUALITY GATE PORTAL
    // =========================================================================
    "mdv.quality_gate_title": "B. Sub-Supplier Quality Gate Portal",
    "mdv.quality_gate_subtitle": "Individual compliance review per sub-supplier — steel origin, RVC, value reconciliation",
    "mdv.gate_flagged": "FLAGGED REJECTION",
    "mdv.gate_clean": "CLEAN PASS",
    "mdv.steel_origin": "Steel Origin",
    "mdv.mill_cert": "Mill Cert",
    "mdv.no_mill_cert": "No mill certificate on file",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — SECTION C: T-MEC READINESS
    // =========================================================================
    "mdv.tmec_readiness_title": "C. 2026 T-MEC Readiness",
    "mdv.tmec_readiness_subtitle": "Paper trail status per sub-supplier — from raw data to T-MEC Certificate",
    "mdv.audit_ready": "Audit Ready",
    "mdv.blocked": "Blocked",
    "mdv.tmec_ready": "AUDIT READY",
    "mdv.tmec_blocked": "BLOCKED",
    "mdv.flow_sub_data": "Sub-Supplier Data",
    "mdv.flow_reconciliation": "Alisio Reconciliation",
    "mdv.flow_mdv": "Manifestación de Valor",
    "mdv.flow_tmec_cert": "T-MEC Certificate",
    "mdv.detail_rvc": "RVC",
    "mdv.detail_steel": "Steel",
    "mdv.detail_mdv": "MdV Docs",
    "mdv.blocked_reason": "BLOCKED: Value discrepancy + unverified steel origin + RVC below threshold. Cannot issue T-MEC Certificate until all flags are resolved.",
    "mdv.tmec_summary": "2 of 3 sub-suppliers are audit-ready for 2026 T-MEC certification. Fundición Industrial Celaya is BLOCKED due to customs value discrepancy, unverified steel origin, and RVC below the 75% core part threshold. Alisio's reconciliation engine detected these issues automatically — before they reached customs.",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — MVE RECONCILIATION ENGINE (Section A cards)
    // =========================================================================
    "mve.reconciliation.description": "For each transaction, Alisio verifies CFDI value, purchase order, and payment confirmation across all sub-suppliers.",
    "mve.reconciliation.match": "MATCH",
    "mve.reconciliation.cannotReconcile": "CANNOT RECONCILE",
    "mve.gate.approved": "Ready for submission",
    "mve.gate.blocked": "Blocked",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — MVE QUALITY GATE PORTAL (Section B split-screen)
    // =========================================================================
    "mve.left_column_header": "SUB-SUPPLIER UPLOAD VIEW",
    "mve.left_column_title": "SUB-SUPPLIER VIEW",
    "mve.right_column_header": "TREMEC / ALISIO VALIDATION",
    "mve.right_column_title": "TREMEC / ALISIO RESULT",
    "mve.uploaded_cfdi_clean": "CFDI uploaded — Alisio validated automatically",
    "mve.uploaded_cfdi_flagged": "CFDI uploaded — Alisio DETECTED ISSUES",
    "mve.cfdi_amount": "CFDI Amount",
    "mve.product": "Product",
    "mve.processing_time": "Processing time",
    "mve.no_password_no_erp": "No password, no ERP",
    "mve.timeline": "Timeline",
    "mve.timeline_upload": "Upload",
    "mve.timeline_validation": "Validation",
    "mve.timeline_result": "Result",
    "mve.auto_notification_sent": "Auto notification sent to supplier",
    "mve.resultado_alisio": "ALISIO RESULT",
    "mve.checks_passed": "checks passed",
    "mve.critical_failures": "critical failures",
    "mve.alerta_origen": "ORIGIN ALERT",
    "mve.check_hs_code": "HS code",
    "mve.pending_review": "pending review",
    "mve.verified": "verified",
    "mve.check_price_lme": "Price vs LME",
    "mve.within_range": "within range",
    "mve.below_range": "below range",
    "mve.check_origin": "Origin",
    "mve.unverifiable": "unverifiable",
    "mve.check_reconciliation": "3-way reconciliation",
    "mve.cannot_reconcile": "cannot reconcile",
    "mve.no_payment_proof": "no payment proof",
    "mve.batch_audit_ready": "Batch: 100% audit-ready",
    "mve.mve_ready": "MVE READY",
    "mve.datos_bloqueados": "DATA BLOCKED",
    "mve.blocked_cannot_proceed": "This transaction will NOT touch TREMEC's MVE until resolved",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — MVE READINESS (Section C rewrite)
    // =========================================================================
    // Section 1: IMMEX Program Health
    "mve.immex_health_title": "IMMEX Program Health",
    "mve.status_label": "Status",
    "mve.status_healthy": "HEALTHY",
    "mve.months_no_discrepancy": "Months without discrepancy",
    "mve.audit_risk": "Audit risk",
    "mve.audit_risk_low": "LOW",
    "mve.key_indicators": "Key Indicators",
    "mve.sub_supplier_txns": "Sub-supplier transactions this period",
    "mve.auto_approved": "Auto-approved (Green)",
    "mve.corrected_approved": "Corrected and approved (Yellow)",
    "mve.blocked_red": "Blocked (Red)",
    "mve.mve_zero_discrepancy": "MVE with zero discrepancies",
    "mve.avg_resolution_time": "Average resolution time",
    "mve.hours": "hours",

    // Section 2: MVE Verification Checklist
    "mve.verification_title": "MVE Verification — Ready for June 2026",
    "mve.digital_infra": "Digital Infrastructure",
    "mve.vucem_access": "VUCEM access",
    "mve.efirma_importer": "e.firma (importer)",
    "mve.e2_format": "E2 format tested",
    "mve.customs_broker": "Customs broker authorized",
    "mve.docs_per_supplier": "Documentation per Sub-Supplier",
    "mve.complete": "complete",
    "mve.docs_missing": "docs missing",
    "mve.regional_content": "Regional Content (RVC)",
    "mve.weighted_avg": "Weighted average",
    "mve.threshold": "threshold",
    "mve.supplier_below": "supplier below threshold",
    "mve.trend_stable": "Trend: stable last 6 months",

    // Section 3: IMMEX Risk Ladder
    "mve.risk_ladder_title": "IMMEX Risk Ladder",
    "mve.without_alisio": "Without Alisio",
    "mve.with_alisio": "With Alisio",
    "mve.risk_step1_no": "Sub-supplier delivers incorrect data",
    "mve.risk_step2_no": "TREMEC declares incorrect value in VUCEM",
    "mve.risk_step3_no": "SAT cross-references — detects discrepancy",
    "mve.risk_step4_no": "Audit — Fines $29K-$107K MXN per operation",
    "mve.risk_step5_no": "Pattern of discrepancies — IMMEX program review",
    "mve.risk_step6_no": "IMMEX REVOCATION — Full duties on all imports — TREMEC cannot compete",
    "mve.risk_step1_yes": "Sub-supplier uploads CFDI — Alisio validates in 30 sec",
    "mve.risk_step2_yes": "Clean data — MVE / Dirty data — BLOCKED",
    "mve.risk_step3_yes": "100% of MVE submitted with zero discrepancies",
    "mve.risk_step4_yes": "Audit — No findings — IMMEX program safe",

    // Generate Report Button
    "mve.generate_report": "Generate MVE Report",
    "mve.report_tooltip": "Generates a full MVE compliance report for TREMEC — demo endpoint",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — MVE EXTENDED KEYS
    // =========================================================================
    "mve.title": "Manifestación de Valor Electrónica",
    "mve.reconciliation.title": "Automatic Reconciliation Engine",
    "mve.reconciliation.cfdi_value": "CFDI Value",
    "mve.reconciliation.purchase_order": "Purchase Order",
    "mve.reconciliation.payment_confirmed": "Payment Confirmed",
    "mve.reconciliation.payment_unconfirmed": "UNCONFIRMED",
    "mve.reconciliation.cannot_reconcile": "CANNOT RECONCILE",
    "mve.reconciliation.unit_price_vs_market": "Unit Price vs Market",
    "mve.reconciliation.origin_verified": "Origin Verified",
    "mve.reconciliation.incoterm_check": "INCOTERM Check",
    "mve.reconciliation.range": "Range",
    "mve.reconciliation.anomaly": "ANOMALY",
    "mve.reconciliation.consistent": "consistent",
    "mve.reconciliation.freight_matches": "Freight matches",
    "mve.reconciliation.no_certificate": "No certificate",
    "mve.reconciliation.unverifiable": "Unverifiable",
    "mve.reconciliation.no_mill_cert": "No mill cert",
    "mve.reconciliation.freight": "Freight",
    "mve.reconciliation.gate_ready": "READY FOR SUBMISSION / LISTA PARA TRANSMISION",
    "mve.reconciliation.gate_blocked": "BLOCKED / BLOQUEADA",
    "mve.reconciliation.checks_passed": "checks passed — all reconciled",
    "mve.reconciliation.critical_failures": "critical failures:",
    "mve.reconciliation.approved": "APPROVED",
    "mve.reconciliation.blocked": "BLOCKED",
    "mve.gate.approved_detail": "All values reconciled. Documentation complete.",
    "mve.gate.blocked_detail": "critical failures. This transaction CANNOT be included in TREMEC's Manifestación de Valor.",
    "mve.gate.pending": "Pending correction",
    "mve.gate.remediation_link": "View remediation details",
    "mve.mve_status": "MVE STATUS",
    "mve.qualityGate.title": "Sub-Supplier Quality Gate",
    "mve.qualityGate.description": "Alisio validates sub-supplier data before it reaches TREMEC's declaration",
    "mve.qualityGate.sub_supplier_view": "SUB-SUPPLIER VIEW",
    "mve.qualityGate.tremec_view": "TREMEC VIEW",
    "mve.qualityGate.uploaded_cfdi": "Uploaded CFDI → Alisio validated automatically",
    "mve.qualityGate.detected_issues": "Uploaded CFDI → Alisio DETECTED ISSUES",
    "mve.qualityGate.processing_time": "Time: 30 seconds",
    "mve.qualityGate.no_password": "No password, no ERP",
    "mve.qualityGate.notification_sent": "Alisio sent automatic notification to supplier requesting:",
    "mve.qualityGate.alisio_result": "ALISIO RESULT",
    "mve.qualityGate.origin_alert": "ORIGIN ALERT",
    "mve.qualityGate.anomaly_detected": "Price anomaly detected. Possible non-originating material outside USMCA zone.",
    "mve.qualityGate.data_blocked": "DATA BLOCKED",
    "mve.qualityGate.data_blocked_detail": "This transaction will NOT touch TREMEC's MVE until resolved",
    "mve.qualityGate.batch_ready": "Batch: 100% audit-ready",
    "mve.qualityGate.hs_verified": "HS code verified",
    "mve.qualityGate.price_in_range": "Price within LME range",
    "mve.qualityGate.origin_check": "Origin:",
    "mve.qualityGate.rvc_check": "RVC:",
    "mve.qualityGate.threshold": "threshold:",
    "mve.qualityGate.reconciliation_ok": "3-way reconciliation OK",
    "mve.qualityGate.price_below": "Price: -41% vs market",
    "mve.qualityGate.no_mill_cert": "No mill certificate",
    "mve.qualityGate.no_contract": "No formal contract",
    "mve.readiness.title": "T-MEC 2026 Readiness",
    "mve.readiness.program_title": "TREMEC — Synchronizer Program",

    // =========================================================================
    // DECISION AUDIT DRAWER
    // =========================================================================
    "audit.what_decided": "What Was Decided",
    "audit.how_calculated": "How It Was Calculated",
    "audit.data_used": "What Data Was Used",
    "audit.rules_applied": "Rules Applied",
    "audit.human_oversight": "Human Oversight",
    "audit.document_chain": "Document Chain",
    "audit.confidence": "Confidence",
    "audit.reviewed_by": "Reviewed by",
    "audit.pending_review": "Awaiting human review — decision not yet finalized",
    "audit.auto_approved": "Auto-approved — confidence above threshold, no anomalies detected",
    "audit.decision_hash": "Decision hash",
    "audit.export_record": "Export Audit Record",

    // =========================================================================
    // MISSING HARDCODED STRINGS — added for full i18n coverage
    // =========================================================================
    "common.new": "NEW",
    "common.rvc": "RVC",
    "common.compliant": "Compliant",
    "common.non_compliant": "Non-compliant",
    "sec.level_prefix": "Level",
    "sec.flow_verify": "Verify",
    "sec.flow_clean": "Clean",
    "sec.flow_package": "Package",
    "sec.flow_oem_bank": "OEM / Bank",
    "katcon.hs_code": "HS Code",

    // KATCON CORRECTION WORKFLOW
    "katcon.portal_title": "Katcon Compliance Portal",
    "katcon.portal_subtitle": "Supplier chain compliance for Katcon's exhaust system programs.",
    "katcon.tab_overview": "Overview",
    "katcon.tab_suppliers": "Suppliers",
    "katcon.tab_alerts": "Alerts",
    "katcon.hs_issues": "HS Code Issues",
    "katcon.mv_ready": "MV Ready",
    "katcon.s232_exposure": "Section 232 Exposure",
    "katcon.apply_correction": "Apply Correction",
    "katcon.correction_title": "HS Code Correction — Live Propagation",
    "katcon.correction_subtitle": "One correction cascades through the entire document chain.",
    "katcon.confirm_correction": "Confirm Correction",
    "katcon.correction_applied": "Correction Applied",
    "katcon.close_return": "Close & Return to Portal",
    "katcon.propagating": "Propagating...",
    "katcon.correction_history": "Correction History",
    "katcon.resolved": "Resolved",

    "tc.instant": "Instant",
    "bank.steel_chain_fmb": "FMB (forge+machine+HT)",
    "bank.steel_chain_tremec": "TREMEC (assembly)",
    "bank.steel_label": "Steel",
    "recv.hero_batch_label": "Batch #4456 — TREMEC",
    "recv.hero_net_90": "NET 90",
    "recv.hero_badge": "HERO",
    "recv.net_prefix": "NET",
    "mve.chain_stopped": "— chain stopped —",
    "tremec.rvc_prefix": "RVC",
    "tremec.ia_badge": "AI",

    // =========================================================================
    // PORTAL SHARED KEYS (Katcon / Nexteer / TREMEC portals)
    // =========================================================================
    "portal.tab_overview": "Overview",
    "portal.tab_suppliers": "Suppliers",
    "portal.tab_alerts": "Alerts",
    "portal.tab_option1": "Option 1: Compliance",
    "portal.tab_option2": "Option 2: Early Payment",
    "portal.tab_digital_passport": "Digital Passport",
    "portal.supplier_compliance_portal": "Supplier Compliance Portal",
    "portal.reset_demo": "Reset Demo",
    "portal.mv_deadline": "MV Deadline: June 1",
    "portal.days": "days",

    // Katcon-specific portal keys
    "katcon.tab_customs": "Customs & 232",
    "katcon.tab_sap": "SAP Integration",
    "katcon.demo_banner": "Demo Mode — Showing representative data for Katcon's supply chain. Connect your SAP for live supplier data.",
    "katcon.edicom_title": "Complemento con EDICOM",

    // =========================================================================
    // TREMEC COMPLIANCE TABS (Overview, USMCA & RVC, Suppliers, Alerts)
    // =========================================================================
    "demo.tab_overview": "Overview",
    "demo.tab_usmca": "USMCA & RVC",
    "demo.tab_suppliers_compliance": "Suppliers",
    "demo.tab_alerts": "Alerts",

    // Overview tab
    "tremec.overview_icts_exposure": "ICTS Exposure",
    "tremec.overview_icts_sub": "supplier(s) with TCU/sensor risk",
    "tremec.overview_avg_rvc": "Avg Transmission RVC",
    "tremec.overview_rvc_sub": "above 75% threshold",
    "tremec.overview_steel_verified": "Steel Origin Verified",
    "tremec.overview_steel_sub": "Melt-and-pour USMCA confirmed",
    "tremec.overview_mv_ready": "MV Ready",
    "tremec.overview_mv_sub": "Manifestación de Valor",
    "tremec.overview_table_title": "TREMEC Supplier Compliance Overview",
    "tremec.overview_th_hts": "HTS Code",
    "tremec.overview_th_icts": "ICTS",
    "tremec.overview_th_risk": "Risk",
    "tremec.overview_th_efos": "EFOS",
    "tremec.overview_th_steel": "Steel",
    "tremec.overview_th_mv": "MV",

    // RFC Screening
    "tremec.rfc_title": "RFC / EFOS 69-B Screening",
    "tremec.rfc_help": "Paste one or more RFC codes to screen against SAT's EFOS 69-B list and Alisio's risk database. Try: FMB850312AAA, CAC880715DDD",
    "tremec.rfc_placeholder": "Enter RFC codes (one per line, or comma-separated)...",
    "tremec.rfc_screening": "Screening",
    "tremec.rfc_run": "Run Screening",
    "tremec.rfc_download": "Download Results",
    "tremec.rfc_clean": "Clean",
    "tremec.rfc_clean_demo": "Clean (demo)",
    "tremec.rfc_unknown": "Unknown",

    // USMCA & RVC tab
    "tremec.usmca_title": "USMCA & RVC Compliance",
    "tremec.scenario_current": "Current Rules (2026)",
    "tremec.scenario_jr2026": "JR 2026 — Proposed",
    "tremec.scenario_2027": "2027 Steel + ICTS",
    "tremec.scenario_current_desc": "USMCA Chapter 4 in force. Core parts: 75%. Roll-up active. Steel: melt-and-pour in USMCA.",
    "tremec.scenario_jr2026_desc": "Core 75%→80%. Roll-up eliminated. Chinese-affiliation disclosure (>25% ownership). Non-originating content traces to vehicle level.",
    "tremec.scenario_2027_desc": "All JR 2026 changes + steel ALL processes in USMCA (Art. 4.6) + MY 2030 ICTS hardware prohibition. TCU-equipped transmissions become covered hardware.",
    "tremec.rvc_dashboard": "TREMEC RVC Dashboard — By Product Line",
    "tremec.margin": "margin",
    "tremec.classification_title": "TREMEC Product Classification — USMCA Core / Principal / Complementary",
    "tremec.th_product_line": "Product Line",
    "tremec.th_category": "Category",
    "tremec.th_threshold": "Threshold",
    "tremec.th_current_rvc": "Current RVC",
    "tremec.th_status": "Status",
    "tremec.th_notes": "Notes",
    "tremec.rollup_overturned": "Roll-up provision overturned.",
    "tremec.rollup_overturned_detail": "Under the proposed rules, non-originating content in transmission subassemblies traces through to the vehicle level. Subassembly-level roll-up no longer shields the final vehicle RVC calculation.",
    "tremec.rollup_traces": "Roll-up overturned: {pct}% non-originating content traces to vehicle level",

    // RVC Calculator
    "tremec.calc_title": "RVC Calculator — Build-Down Method",
    "tremec.calc_desc": "RVC = ((TV - VNM) / TV) × 100. For transmissions (core parts), minimum 75% under current rules.",
    "tremec.calc_tv": "Transaction Value (TV)",
    "tremec.calc_vnm": "Non-Originating Materials (VNM)",
    "tremec.calc_pass": "PASS",
    "tremec.calc_fail": "FAIL",
    "tremec.calc_above": "above threshold",
    "tremec.calc_below": "below threshold",

    // Steel Origin
    "tremec.steel_title": "Steel & Aluminum Origin Verification",
    "tremec.steel_all_processes": "ALL PROCESSES REQUIRED",
    "tremec.th_material": "Material",
    "tremec.th_origin": "Origin",
    "tremec.th_mill_cert": "Mill Cert",
    "tremec.th_s232_exposure": "§232 Exposure",
    "tremec.th_melt_pour": "Melt & Pour",
    "tremec.th_all_processes": "All Processes",
    "tremec.steel_verified": "Verified",
    "tremec.steel_non_usmca": "Non-USMCA",
    "tremec.steel_art46_title": "Article 4.6 tightening (Jul 2027):",
    "tremec.steel_art46_detail": "Steel must have ALL processing steps (melting, pouring, casting, hot-rolling, cold-rolling) performed in a USMCA country. Current \"melt-and-pour\" standard will no longer suffice. Re-rolled or re-processed Chinese-origin steel is disqualified.",

    // Section 232
    "tremec.s232_title": "Section 232 Steel Tariff Exposure",
    "tremec.s232_rate": "Current Rate",
    "tremec.s232_rate_desc": "on non-USMCA steel",
    "tremec.s232_total": "Total Exposure",
    "tremec.s232_total_desc": "unverified suppliers",
    "tremec.s232_at_risk": "Suppliers at Risk",
    "tremec.s232_at_risk_desc": "missing certs",

    // Suppliers tab
    "tremec.suppliers_title": "TREMEC Supplier Compliance Detail",
    "tremec.icts_assessment": "ICTS Risk Assessment",
    "tremec.rvc_contribution": "RVC Contribution",
    "tremec.relocation_progress": "Relocation Progress",
    "tremec.s232_exposure": "§232 Tariff Exposure",
    "tremec.s232_tariff_desc": "50% tariff on non-USMCA steel",
    "tremec.on_file": "On file",

    // Alerts tab
    "tremec.alerts_title": "TREMEC Compliance Alerts",
    "tremec.alerts_critical": "Critical — Immediate Action Required",
    "tremec.alerts_warning": "Warnings — Action Recommended",
    "tremec.alerts_info": "Informational — Monitor",
    "tremec.alert_critical": "Critical",
    "tremec.alert_warning": "Warning",
    "tremec.alert_info": "Info",
  },

  es: {
    // =========================================================================
    // DEMO BANNER
    // =========================================================================
    "tremec.banner.text": "Modo Demo — Mostrando datos representativos de la cadena de suministro de TREMEC en QRO. Conecte su ERP para datos en vivo.",
    "tremec.banner.reset": "Reiniciar Demo",

    // =========================================================================
    // COMMON
    // =========================================================================
    "common.ready": "Listo",
    "common.pending": "Pendiente",
    "common.verified": "Verificado",
    "common.originating": "Originario",
    "common.ytd": "Acumulado",
    "common.pass": "Aprobado",
    "common.borderline": "Límite",
    "common.above_threshold": "Arriba del umbral de 75%",
    "common.at_threshold": "En el umbral — monitorear",
    "common.flagged": "MARCADO",
    "common.supplier": "Proveedor",
    "common.product": "Producto",
    "common.steel": "Acero",
    "common.status": "Estado",

    // =========================================================================
    // SUPPLIER VIEW
    // =========================================================================
    "supplier.tab_quote": "Cotización",
    "supplier.tab_invoice": "Factura",

    // =========================================================================
    // QUOTE FLOW
    // =========================================================================
    "quote.check_sat": "RFC SAT activo — VIGENTE",
    "quote.check_repse": "Registro REPSE vigente",
    "quote.check_steel": "Fuente de acero: Ternium MX ✓",
    "quote.check_rvc": "VCR estimado: 77.1% (umbral 75%)",
    "quote.check_steel_pct": "Acero originario: 84.3% (umbral 70%)",
    "quote.check_hs": "HS 8483.40 — Elegible T-MEC",
    "quote.rfq_title": "SOLICITUD DE COTIZACIÓN — TREMEC",
    "quote.rfq_number": "RFQ #",
    "quote.product": "Producto",
    "quote.product_name": "Maza de Sincronizador — SAE 8620",
    "quote.qty": "Cant.",
    "quote.qty_value": "2,400 pzas",
    "quote.drawing": "Plano",
    "quote.program": "Programa",
    "quote.submit": "Enviar Cotización a TREMEC",
    "quote.prequalifying": "PRE-CALIFICANDO...",
    "quote.prequalified": "PRE-CALIFICADO — Listo T-MEC",
    "quote.unit_price": "Precio Unitario",
    "quote.total": "Total",
    "quote.est_rvc": "VCR Est.",
    "quote.prequalified_time": "Pre-calificado en 14 segundos",

    // =========================================================================
    // QUOTE STEP 2 — AI BUILD ANIMATION
    // =========================================================================
    "quote.ai_building": "IA CONSTRUYENDO COTIZACIÓN...",
    "quote.ai_building_sub": "Consultando 847 OCs de TREMEC, perfil SAT, datos de capacidad",
    "quote.field_unit_price": "Precio Unitario",
    "quote.field_unit_price_value": "MXN $597.55",
    "quote.field_unit_price_source": "IA — 847 OCs con TREMEC",
    "quote.field_lead_time": "Tiempo de Entrega",
    "quote.field_lead_time_value": "28 días",
    "quote.field_lead_time_source": "IA — capacidad actual 74%",
    "quote.field_steel": "Fuente de Acero",
    "quote.field_steel_value": "Ternium México — Monterrey",
    "quote.field_steel_source": "Perfil — verificado SAT",
    "quote.field_rvc": "Cálculo VCR",
    "quote.field_rvc_value": "76.2%",
    "quote.field_rvc_source": "IA — Método Costo Neto",
    "quote.field_cost": "Desglose de Costos",
    "quote.field_cost_value": "Material $187 / Forja $205 / Maquinado $113 / TT $39",
    "quote.field_cost_source": "Perfil",
    "quote.field_iatf": "Certificación IATF",
    "quote.field_iatf_value": "IATF 16949 vigente 2027-08",
    "quote.field_iatf_source": "Perfil — en archivo",
    "quote.field_cert": "Preparación de Cert.",
    "quote.field_cert_value": "Generado en 28 segundos",
    "quote.field_cert_source": "IA — Auto",
    "quote.field_payment": "Términos de Pago",
    "quote.field_payment_value": "NET 90",
    "quote.field_payment_source": "EDI — estándar TREMEC",
    "quote.steel_origin_badge": "Origen de acero verificado: Ternium Monterrey, 84.3% originario — supera umbral de 70%",
    "quote.rvc_badge": "VCR 76.2% — supera umbral de 75% para partes centrales",
    "quote.submit_to_tremec": "Enviar Cotización a TREMEC →",

    // =========================================================================
    // QUOTE STEP 3 — TREMEC FEEDBACK
    // =========================================================================
    "quote.tremec_reviewing": "TREMEC revisando cotización...",
    "quote.waiting_purchaser_review": "Esperando que TREMEC revise en la vista del Comprador...",
    "quote.feedback_received": "RETROALIMENTACIÓN DE TREMEC RECIBIDA",
    "quote.feedback_from": "Carlos Mendez — Gerente de Compras",
    "quote.draft_responses": "Redactar Respuestas IA →",

    // =========================================================================
    // QUOTE STEP 4 — AI FEEDBACK RESPONSE
    // =========================================================================
    "quote.ai_responding": "IA REDACTANDO RESPUESTAS...",
    "quote.response_traceability_title": "Trazabilidad de Lotes de Acero",
    "quote.response_traceability": "3 lotes Ternium en archivo: TERN-MTY-2026-0892, TERN-MTY-2025-1847, TERN-MTY-2025-1203. Cadena completa: Ternium MTY → FMB forja+maquinado+TT → TREMEC ensamble. Todos los certs vigentes.",
    "quote.response_traceability_source": "Perfil — verificado",
    "quote.response_payment_title": "Análisis NET 60",
    "quote.response_payment": "Factoraje NAFIN cubre la brecha de MXN $385K al 2.5% de descuento. NET 60 aceptado — Poniente IA confirma flujo de caja positivo con factoraje.",
    "quote.response_payment_source": "IA — Análisis Poniente",
    "quote.approve_send": "Aprobar y Enviar a TREMEC →",

    // =========================================================================
    // INVOICE FLOW
    // =========================================================================
    "invoice.upload_title": "Subir Factura CFDI",
    "invoice.upload_subtitle": "Archivo XML de tu sistema contable",
    "invoice.upload_btn": "Subir CFDI XML",
    "invoice.validated": "CFDI Validado — Todos los Campos Coinciden",

    // =========================================================================
    // NINE-POINT FORM
    // =========================================================================
    "nine_point.title": "Formulario de Verificación de 9 Puntos",
    "nine_point.pt1_label": "Nombre y Dirección del Vendedor",
    "nine_point.pt2_label": "ID Fiscal del Vendedor",
    "nine_point.pt2_value": "RFC: FMB120523LK7 — SAT: VIGENTE",
    "nine_point.pt3_label": "Nombre y Dirección del Comprador",
    "nine_point.pt4_label": "Descripción de Mercancías",
    "nine_point.pt4_value": "Maza de sincronizador forjada SAE 8620 CrMo — 2,400 pzas — HS 8483.40.50",
    "nine_point.pt5_label": "Código HS y VCR",
    "nine_point.pt5_value": "HS 8483.40.50 — VCR 76.2% (Costo Neto) — Umbral 75%",
    "nine_point.pt6_label": "Período de Cobertura",
    "nine_point.pt7_label": "Descripción de Bienes",
    "nine_point.pt8_label": "Cálculo VCR Costo Neto",
    "nine_point.pt8_value": "Material MXN $187.40 + Forja $205.00 + Maquinado $112.80 + TT $38.50 — No-orig: $53.85 — VCR: 76.2%",
    "nine_point.source_profile": "Perfil",
    "nine_point.source_edi": "EDI",
    "nine_point.source_cfdi": "CFDI",
    "nine_point.source_ai": "IA",
    "nine_point.source_you": "Tú",
    "nine_point.auto_filled": "8/9 auto-llenados",
    "nine_point.pt9_label": "Firma Autorizada",
    "nine_point.pt9_pending": "Firma requerida",
    "nine_point.legend": "Perfil | EDI | CFDI | IA | Tu",
    "nine_point.sign_btn": "Firmar Certificado →",

    // =========================================================================
    // SIGNATURE
    // =========================================================================
    "signature.clear": "Limpiar",
    "signature.confirm": "Confirmar Firma",

    // =========================================================================
    // COMPLETE
    // =========================================================================
    "complete.title": "Certificado Completo",
    "complete.verified": "Formulario de 9 puntos verificado y sellado",
    "complete.time": "32 seg",
    "complete.sealed": "Sellado SHA-256",
    "complete.sent": "Enviado a TREMEC",

    // =========================================================================
    // PURCHASER VIEW (TREMEC)
    // =========================================================================
    "tremec.title": "Centro de Control de Cumplimiento TREMEC",
    "tremec.live": "EN VIVO",
    "tremec.corridor": "Corredor QRO ↔ Detroit — Revisión Conjunta 2026",
    "tremec.review_score": "Puntuación de Revisión",
    "tremec.stat_shipments": "Envíos",
    "tremec.stat_verified": "T-MEC Verificado",
    "tremec.stat_target": "Meta: 95%",
    "tremec.stat_cert_time": "Tiempo Prom. Cert.",
    "tremec.stat_vs_manual": "vs 4.2 hrs manual",
    "tremec.stat_review": "Puntuación Revisión 2026",
    "tremec.activity_feed": "Actividad de Proveedores",
    "tremec.event_quote": "COTIZACIÓN",
    "tremec.event_quote_received": "Cotización Recibida",
    "tremec.event_quote_detail": "2,400 Mazas Sincronizador — SAE 8620 — MXN $1,663,579 — VCR estimado 77.1%",
    "tremec.award_po": "Asignar OC",
    "tremec.event_cfdi": "CFDI Subido",
    "tremec.event_cfdi_detail": "CFDI 4.0 validado — Lote #4456 — MXN $1,663,579 — RFC coincide",
    "tremec.event_signed": "Certificado Firmado",
    "tremec.event_signed_detail": "Formulario 9 puntos completo — VCR 76.2% verificado — Acero: Ternium MX",
    "tremec.flagged_title": "VCR 68.4% — Debajo del Umbral de 75%",
    "tremec.flagged_detail": "Engranajes SAE 4140 — Origen de acero NO VERIFICADO (Aceros Nacionales) — Precio 41% debajo de LME",
    "tremec.flagged_action": "Acción: Solicitar certificado de molino — No se puede enviar libre de arancel sin origen verificado",
    "tremec.recent_verifications": "Verificaciones Recientes — Todos los Proveedores",

    // Purchaser View — new enhanced events
    "tremec.compliance_steel": "Acero: Ternium Monterrey — 84.3% originario (umbral 70%)",
    "tremec.compliance_rvc": "VCR: 76.2% — supera umbral de 75% para partes centrales",
    "tremec.compliance_iatf": "IATF 16949 — en archivo, válido hasta 2027-08",
    "tremec.event_review": "REVISIÓN",
    "tremec.event_review_title": "Cotización en Revisión",
    "tremec.event_review_detail": "Carlos Mendez revisando cotización FMB — 2 comentarios pendientes",
    "tremec.send_feedback": "Enviar Retroalimentación al Proveedor",
    "tremec.event_po": "OC ASIGNADA",
    "tremec.event_po_title": "Orden de Compra Asignada — TRE-2026-PO-1847",
    "tremec.event_po_detail": "NET 60 — MXN $1,663,579 — Todos los campos de cumplimiento pre-llenados de la cotización",
    "tremec.award_po_btn": "Asignar OC →",
    "tremec.po_awarded_label": "OC Asignada",
    "tremec.feedback_sent": "Retroalimentación enviada al proveedor",

    // =========================================================================
    // BANK VIEW
    // =========================================================================
    "bank.title": "Expediente Digital de Cumplimiento",
    "bank.batch": "Lote #4456",
    "bank.subtitle": "Forjas del Bajío → TREMEC — Mazas Sincronizador SAE 8620",
    "bank.jacket_ready": "Expediente Listo",
    "bank.signature_pending": "Firma Pendiente",
    "bank.funding": "Recomendación de Financiamiento",
    "bank.not_eligible": "No elegible — Esperando firma de 9 puntos del proveedor",
    "bank.amount": "Monto de Factura",
    "bank.advance_rate": "Tasa de Anticipo",
    "bank.available": "Disponible",
    "bank.risk_score": "Puntuación de Riesgo",
    "bank.buyer_rating": "Calificación del Comprador",
    "bank.maturity": "Vencimiento",
    "bank.days": "días",
    "bank.triple_source": "Verificación de Triple Fuente",
    "bank.sat_label": "SAT (Autoridad Fiscal)",
    "bank.sat_detail": "RFC: FMB120523LK7 VIGENTE — UUID CFDI coincide — Monto: MXN $1,663,579",
    "bank.edi_label": "EDI (856 ASN)",
    "bank.edi_detail": "OC# TRE-2026-PO-1847 — 2,400 unidades × MXN $597.55 — Coincide",
    "bank.portal_label": "Portal del Comprador (TREMEC)",
    "bank.portal_detail": "Estado del portal: APROBADO — Fecha de pago: 6 de mayo, 2026 — Monto coincide",
    "bank.full_consensus": "CONSENSO COMPLETO — 3/3 Fuentes Verificadas",
    "bank.steel_origin": "Origen y Trazabilidad del Acero",
    "bank.steel_requirement": "Requisito T-MEC: El acero debe ser fundido y vaciado en territorio T-MEC. Contenido originario ≥ 70%.",
    "bank.steel_grade": "Grado de Acero",
    "bank.steel_mill": "Planta",
    "bank.steel_cert": "Cert. de Planta",
    "bank.steel_lme": "Variación LME",
    "bank.steel_ytd": "Acumulado",
    "bank.steel_traceability": "Trazabilidad de cadena de suministro:",
    "bank.vendor_codes": "Verificación de Códigos de Proveedor",
    "bank.buyer": "Comprador",
    "bank.assigned": "Asignado",
    "bank.pos": "OCs",
    "bank.volume": "Volumen",
    "bank.vendor_commentary": "Relación de 11 años con TREMEC (847 OCs). Kirchhoff agregado en 2019 para diversificación de compradores. Ambos códigos activos y verificados vía referencia cruzada ERP.",
    "bank.audit_trail": "Pista de Auditoría Inmutable",
    "bank.export_pdf": "Exportar PDF",
    "bank.export_json": "Exportar JSON",
    "bank.export_core": "Exportar CORE",

    // =========================================================================
    // TRADE CONNECT VIEW
    // =========================================================================
    "tc.doc_1": "CFDI 4.0 XML — Validado",
    "tc.doc_2": "Certificado T-MEC de 9 Puntos",
    "tc.doc_3": "Lista de Materiales + Cálculo VCR",
    "tc.doc_4": "Reporte de Verificación Triple Fuente",
    "tc.doc_5": "Origen del Acero: Cert. Planta Ternium MTY",
    "tc.doc_6": "EDI 856 ASN — Coincidencia OC TREMEC",
    "tc.doc_7": "Perfil del Proveedor — FMB (82/100)",
    "tc.title": "Sidecar TradeConnect",
    "tc.subtitle": "El paquete completo de datos que acompaña la factura — de Forjas del Bajío a TREMEC a cualquier socio financiero.",
    "tc.sidecar_from": "Forjas del Bajío",
    "tc.sidecar_middle": "sidecar alisio",
    "tc.sidecar_to": "TREMEC / Banco",
    "tc.data_package": "Paquete de Datos",
    "tc.exporting": "Exportando...",
    "tc.export_btn": "Exportar Paquete Sidecar",
    "tc.export_success": "Sidecar Exportado Exitosamente",
    "tc.pt9_required": "Se requiere firma del Pt.9 antes de exportar",
    "tc.kill_shot": "Cada factura lleva su propia prueba. Sin llamadas. Sin adjuntos PDF. Sin 'me puedes reenviar el certificado.' Los datos simplemente viajan.",

    // =========================================================================
    // SECRETARY VIEW
    // =========================================================================
    "sec.comparison_today": "Hoy (sin Alisio)",
    "sec.comparison_with_alisio": "Con Alisio",
    "sec.comp_no_financing": "Sin financiamiento de cuentas por cobrar",
    "sec.comp_nafin_factoring": "Factoraje NAFIN — mismo día",
    "sec.comp_informal": "Factoraje informal (5-8%)",
    "sec.comp_bancomext": "Bancomext pre-calificado",
    "sec.comp_nafin": "Tasas NAFIN (2.5%)",
    "sec.comp_discount": "2.5% — mejor que informal",
    "sec.comp_devbank": "Sin acceso a banca de desarrollo",
    "sec.problem_title": "El Problema",
    "sec.problem_text": "80% de los proveedores automotrices T1 mexicanos no pueden acceder a financiamiento institucional. Tienen los pedidos, las certificaciones, las relaciones verificadas — pero no tienen prueba digital que un banco pueda confiar.",
    "sec.company_title": "La Empresa",
    "sec.founded": "Fundada",
    "sec.employees": "Empleados",
    "sec.revenue": "Ingresos",
    "sec.score": "Puntuación Alisio",
    "sec.roberto_problem": "Roberto Calderón tiene MXN $4.5M flotando en términos de 90 días de TREMEC. Paga nómina cada 15 días (MXN $385K). Sin factoraje, pide prestado informalmente al 5-8% o retrasa pagos a proveedores — ambos perjudican su puntuación y su crecimiento.",
    "sec.capital_stack": "La Escalera de Capital",
    "sec.below_level1_title": "Sin Prueba Digital",
    "sec.below_level1_text": "Aquí es donde está el 80% de los proveedores T1 hoy. Tienen OCs de TREMEC, acero verificado, IATF 16949 — pero no pueden empaquetarlo para un banco. El resultado: factoraje informal al 5-8%, o simplemente esperar 90 días.",
    "sec.level1_title": "Factoraje NAFIN",
    "sec.level1_status": "CALIFICADO",
    "sec.level1_without": "Sin Alisio, Roberto tiene que recopilar su CFDI manualmente, probar el origen de su acero, coordinarse con el portal de TREMEC, y esperar que el banco confíe en sus documentos. Con Alisio: Verificación Triple Fuente + Expediente Digital = elegibilidad instantánea.",
    "sec.level1_this_invoice": "Esta factura califica AHORA. Roberto puede factorarla hoy.",
    "sec.level2_title": "Crédito PyMEx Bancomext",
    "sec.level2_status": "AL ALCANCE",
    "sec.level2_gap": "Brecha: Necesita estados financieros auditados. Alisio puede auto-generar el resumen financiero del historial verificado.",
    "sec.level3_title": "Línea de Capital de Trabajo",
    "sec.level3_status": "FUTURO",
    "sec.level3_text": "Requiere 18+ meses de historial de transacciones verificadas en plataforma Alisio.",
    "sec.level4_title": "Financiamiento de Equipos",
    "sec.level4_status": "FUTURO",
    "sec.level4_text": "Capacidad CNC adicional (Mazak / Haas). Requiere Nivel 2 desbloqueado + avalúo de equipos.",
    "sec.level5_title": "Crédito a Plazo Bancomext",
    "sec.level5_status": "FUTURO",
    "sec.level5_text": "Requiere 3+ años de historial, estados financieros auditados, documentación de exportación. Alisio construye el expediente automáticamente con el tiempo.",
    "sec.live_proof": "Lo Que Acabamos de Probar — En Vivo",
    "sec.live_1": "TREMEC envió una solicitud → Tell Tale la clasificó en 2 segundos",
    "sec.live_2": "El certificado T-MEC de 9 puntos de Roberto se llenó 8/9 automáticamente con datos existentes",
    "sec.live_3": "Verificación Triple Fuente: SAT + EDI + Portal = Consenso COMPLETO",
    "sec.live_4": "Expediente bancario generado al instante — MXN $1,414,042 disponibles hoy",
    "sec.live_conclusion": "Roberto no necesita una línea de crédito más grande. Necesita prueba de que su negocio existente es real. Alisio proporciona esa prueba.",
    "sec.policy_title": "Implicación de Política",
    "sec.policy_text": "NAFIN y Bancomext ya tienen los programas. La brecha no es capital — es infraestructura digital. Alisio cierra esa brecha para cada proveedor en el corredor TREMEC.",
    "sec.policy_impact": "Si cada proveedor T1 en el corredor Querétaro-Detroit tuviera acceso a Alisio, estimamos MXN $2.4B en capital de trabajo desbloqueado. Eso no es dinero nuevo — es dinero que ya se ganó pero es inaccesible.",
    "sec.sba_title": "Comparación con SBA",
    "sec.sba_text": "La Administración de Pequeños Negocios de EE.UU. (SBA) proporciona garantías de préstamos para pequeños fabricantes. México tiene programas equivalentes a través de NAFIN y Bancomext, pero la adopción es baja porque falta la capa de prueba digital. Alisio llena el mismo rol que las plataformas de suscripción automatizada llenan para prestamistas SBA en EE.UU.",
    "sec.closing": "No estamos pidiendo nueva política. Estamos pidiendo reconocimiento de que la infraestructura para conectar política existente con proveedores existentes ahora existe. Alisio es esa infraestructura.",
    "sec.check_rfc_active": "RFC activo (FMB120523LK7 — VIGENTE)",
    "sec.check_supplier_gran_empresa": "Proveedor de Gran Empresa (TREMEC califica)",
    "sec.check_signed_contract": "Contrato de factoraje firmado",
    "sec.check_2yr_operating": "2+ años operando (13 años)",
    "sec.check_export_activity": "Actividad exportadora (TREMEC → Novi, MI)",
    "sec.check_financial_statements": "Estados financieros (los tiene, no auditados)",
    "sec.level1_banks": "BBVA / Banorte / Monex vía NAFIN",
    "sec.level2_banks": "Bancomext — Hasta $3M USD",
    "sec.level3_amount": "Hasta MXN $10M (Banco comercial + garantía NAFIN)",
    "sec.level5_amount": "Hasta MXN $60M, plazos a 5 años",
    "sec.location": "Circuito El Marqués Norte #47, P.I. El Marqués, QRO",
    "sec.industry": "Forja cerrada + Maquinado CNC + Tratamiento térmico",

    // SECRETARY — VERIFICATION LAYER
    "sec.verification_title": "La Capa de Verificación",
    "sec.verification_subtitle": "Lo que Alisio verifica — automáticamente, en tiempo real",
    "sec.verify_sat": "Estado SAT/RFC — Activo, validado contra autoridad fiscal",
    "sec.verify_cfdi": "CFDI 4.0 — UUID, montos, cruce de RFC",
    "sec.verify_steel": "Origen del Acero — Certificado de molino, fundido y vaciado en territorio T-MEC",
    "sec.verify_rvc": "Cálculo VCR — Método costo neto, cumplimiento de umbral",
    "sec.verify_edi": "EDI/ASN — Orden de compra, cantidades, precios conciliados",
    "sec.verify_portal": "Portal del Comprador — Estado de aprobación, fecha de pago confirmada",
    "sec.verify_9pt": "Formulario T-MEC de 9 Puntos — 8/9 campos auto-llenados con datos verificados",
    "sec.verify_mdv": "Manifestación de Valor — Reconciliación tripartita de valor por sub-proveedor",
    "sec.verify_time": "Verificación promedio: 32 segundos — vs 4.2 horas manual",

    // SECRETARY — T1 ↔ T2 COLLABORATION
    "sec.collab_title": "Cómo Trabajan Juntos T1s y T2s",
    "sec.collab_subtitle": "Alisio se ubica entre niveles — verificando datos antes de que suban en la cadena",
    "sec.collab_t2_title": "Para el Sub-Proveedor T2",
    "sec.collab_t2_1": "Sin ERP necesario — solo sube un CFDI",
    "sec.collab_t2_2": "Validado en 30 segundos, no en 30 días",
    "sec.collab_t2_3": "Notificación automática si los documentos necesitan corrección",
    "sec.collab_t2_4": "Pago más rápido — T1 puede factorizar con cadena verificada",
    "sec.collab_t1_title": "Para el Proveedor T1",
    "sec.collab_t1_1": "Screening automático de cumplimiento de sub-proveedores",
    "sec.collab_t1_2": "Protección IMMEX — datos incorrectos bloqueados antes de llegar a VUCEM",
    "sec.collab_t1_3": "Documentación lista para auditoría por sub-proveedor",
    "sec.collab_t1_4": "Pre-calificado para financiamiento con cadena verificada",
    "sec.collab_example": "En esta demo: Alisio validó 3 sub-proveedores para el programa de sincronizadores de TREMEC. 2 aprobaron automáticamente. 1 fue bloqueado — antes de que datos incorrectos llegaran a aduanas.",

    // SECRETARY — REGIONAL ECONOMIC DEVELOPMENT
    "sec.regional_title": "Desarrollo Económico Regional",
    "sec.regional_corridor": "El Corredor Querétaro-Detroit",
    "sec.regional_corridor_text": "200+ fabricantes de autopartes en Querétaro. OEMs principales: TREMEC, Kirchhoff, Dana, Martinrea. Este corredor es la columna vertebral manufacturera del T-MEC.",
    "sec.regional_multiplier": "El Efecto Multiplicador",
    "sec.regional_multiplier_text": "Cuando FMB factoriza una factura de TREMEC, le paga a Ternium (acero) más rápido. Ternium mantiene capacidad. FMB invierte en equipos y contrata trabajadores. Más nómina significa más gasto local.",
    "sec.regional_nearshoring": "Preparación para Nearshoring",
    "sec.regional_nearshoring_text": "Empresas que mueven producción a México necesitan cadenas de suministro verificadas. Alisio proporciona la infraestructura digital que hace a los proveedores mexicanos bancables y listos para auditoría ante nuevos programas OEM.",
    "sec.regional_formalization": "Formalización de la Cadena de Suministro",
    "sec.regional_formalization_text": "Cada CFDI validado es un dato más en el perfil crediticio del proveedor. Con el tiempo: más datos verificados → mejor crédito → capital más barato → más crecimiento. Alisio integra a proveedores T2 y T3 al sistema financiero formal.",
    "sec.regional_impact": "Estimados MXN $2,400M en capital de trabajo desbloqueado si cada proveedor T1 en el corredor Querétaro-Detroit tuviera acceso a Alisio. No es dinero nuevo — es dinero ya ganado pero actualmente inaccesible.",

    // =========================================================================
    // DEMO PAGE TABS
    // =========================================================================
    "demo.tab_supplier": "Proveedor",
    "demo.tab_purchaser": "Comprador",
    "demo.tab_bank": "Banco",
    "demo.tab_tradeconnect": "TradeConnect",
    "demo.tab_secretary": "Sec. Economía",
    "demo.tab_receivables": "Cuentas por Cobrar",
    "demo.tab_manifestacion": "Manifestación de Valor",
    "demo.reset": "Reiniciar Demo",

    // =========================================================================
    // RECEIVABLES VIEW
    // =========================================================================
    "recv.title": "Cuentas por Cobrar",
    "recv.fmb_perspective": "Perspectiva FMB",
    "recv.subtitle": "Facturas pendientes, antigüedad, DSO y elegibilidad para factoraje del portafolio de compradores de FMB.",
    "recv.stat_outstanding": "Total Pendiente",
    "recv.stat_invoices": "facturas",
    "recv.stat_avg_dso": "DSO Promedio Ponderado",
    "recv.days": "días",
    "recv.stat_dso_sub": "Ponderado por monto de factura",
    "recv.stat_factorable": "Monto Factorable",
    "recv.stat_verified_invoices": "facturas verificadas",
    "recv.stat_flagged": "Marcadas",
    "recv.stat_flagged_sub": "Bloqueadas para factoraje",
    "recv.aging_title": "Análisis de Antigüedad",
    "recv.current": "Vigente",
    "recv.table_title": "Detalle de Facturas",
    "recv.col_invoice": "Factura",
    "recv.col_buyer": "Comprador",
    "recv.col_description": "Descripción",
    "recv.col_amount": "Monto",
    "recv.col_terms": "Plazo",
    "recv.col_days_left": "Días Rest.",
    "recv.status_verified": "Verificada",
    "recv.status_flagged": "Marcada",
    "recv.status_pending": "Pendiente",
    "recv.hero_factored": "Factura Principal Factorada",
    "recv.hero_step_sat": "Verificación SAT — Vigente",
    "recv.hero_step_cfdi": "Timbrado CFDI confirmado",
    "recv.hero_step_cert": "Certificado de Origen USMCA firmado",
    "recv.hero_step_po": "OC otorgada por TREMEC",
    "recv.hero_step_factor": "Factoraje disponible — 85% de anticipo",
    "recv.filter_buyer": "Todos los Compradores",
    "recv.filter_label": "Filtrar por Comprador",
    "recv.days_to_due": "Días al vencimiento",
    "recv.concentration_title": "Concentración de Compradores",
    "recv.concentration_note": "Portafolio de tres compradores reduce riesgo de concentración. TREMEC en 62% (antes 78%). Katcon y Kirchhoff diversifican cuentas por cobrar y mejoran términos de crédito.",
    "recv.inv_hero": "Hub Sincronizador — Lote #4456 (SAE 8620)",
    "recv.inv_celaya": "Blancos de Engranes — Sub-proveedor Celaya (MARCADO: sin cert. molino)",
    "recv.inv_aceros": "Coronas Forjadas — Aceros del Bajío",
    "recv.inv_troquelados": "Manguitos Sincronizadores — Troquelados Irapuato",
    "recv.inv_cnc": "Hubs Maquinados CNC — Complementos Maq. del Bajío",
    "recv.inv_componentes": "Acoplamientos Tratados — Componentes Auto del Centro",
    "recv.inv_metalicos": "Flechas de Precisión — Metálicos Industriales Salamanca",
    "recv.inv_kirchhoff": "Soportes Estructurales — Kirchhoff OC #K-2241",
    "recv.inv_katcon": "Bridas de Escape Forjadas — Katcon Programa SCR Euro 7",

    // =========================================================================
    // DEMO PAGE TABS — KATCON
    // =========================================================================
    "demo.tab_katcon_buyer": "Katcon Comprador",
    "demo.tab_katcon_passport": "Pasaporte Digital",

    // =========================================================================
    // KATCON BUYER VIEW
    // =========================================================================
    "katcon.buyer_title": "Cumplimiento del Proveedor — FMB",
    "katcon.buyer_subtitle": "Vista de Katcon sobre el cumplimiento de FMB para el programa de bridas de escape. Datos de TREMEC no visibles.",
    "katcon.supplier_qualified": "Proveedor Calificado",
    "katcon.current_po": "Valor OC Actual",
    "katcon.units": "unidades",
    "katcon.threshold": "Umbral",
    "katcon.sat_status": "Estado SAT",
    "katcon.eu_dpp": "Exportar DPP UE",
    "katcon.eu_dpp_sub": "Pasaporte + Alisio listo",
    "katcon.rvc_title": "Contenido de Valor Regional USMCA",
    "katcon.part_category": "Categoría de Parte",
    "katcon.complementary_part": "Parte Complementaria",
    "katcon.method": "Método",
    "katcon.net_cost": "Costo Neto",
    "katcon.originating_materials": "Materiales Originarios",
    "katcon.vnm_per_unit": "VNM / Unidad",
    "katcon.margin": "Margen Sobre Umbral",
    "katcon.rvc_note": "Parte complementaria — umbral 65% Costo Neto aplica según USMCA Anexo 4-B Tabla A.2. Margen cómodo de 16.4 puntos.",
    "katcon.steel_origin": "Verificación Origen del Acero",
    "katcon.mill": "Fundidora",
    "katcon.mill_cert": "Cert. Molino",
    "katcon.heat_lot": "Lote de Calor",
    "katcon.melted_poured": "Fundido y Vaciado",
    "katcon.compliant": "Cumple",
    "katcon.sat_verification": "Verificación SAT / CFDI",
    "katcon.issuer": "Emisor",
    "katcon.receiver": "Receptor",
    "katcon.clean": "Limpio",
    "katcon.sub_supplier_chain": "Cadena de Cumplimiento Sub-Proveedores",
    "katcon.material_input": "Material / Insumo",
    "katcon.passport_overlay_title": "Pasaporte Digital + Alisio",
    "katcon.passport_overlay_text": "Su pasaporte rastrea trazabilidad interna. Alisio agrega: preparación para auditoría USMCA, verificación de sub-proveedores, bancabilidad. Cambie a la pestaña Pasaporte Digital para ver la capa completa.",

    // =========================================================================
    // KATCON DIGITAL PASSPORT VIEW
    // =========================================================================
    "passport.title": "Pasaporte Digital del Producto",
    "passport.subtitle": "Datos internos del pasaporte Katcon (SAP S/4HANA) con capa de cumplimiento Alisio",
    "passport.internal_label": "Pasaporte Interno Katcon",
    "passport.alisio_overlay": "Verificación Alisio",
    "passport.overlay_sub": "Lo que el pasaporte NO hace",
    "passport.compliance_score": "Puntuación General de Cumplimiento",
    "passport.material_origin": "Origen y Composición del Material",
    "passport.grade": "Grado",
    "passport.supplier": "Proveedor",
    "passport.heat_lot": "Lote de Calor",
    "passport.composition": "Composición",
    "passport.weight": "Peso / Unidad",
    "passport.manufacturing": "Proceso de Manufactura",
    "passport.process": "Proceso",
    "passport.forge_temp": "Temp. Forja",
    "passport.forge_pressure": "Presión Forja",
    "passport.die_number": "No. de Dado",
    "passport.heat_treatment": "Tratamiento Térmico",
    "passport.quality": "Registros de Calidad",
    "passport.dimensional": "Dimensional",
    "passport.hardness": "Dureza",
    "passport.surface": "Acabado Superficial",
    "passport.carbon": "Huella de Carbono",
    "passport.rvc_verification": "Cálculo RVC (Alisio)",
    "passport.rvc_passport_note": "El pasaporte rastrea orígenes de materiales pero no calcula el RVC USMCA. Alisio clasifica materiales originarios vs no originarios y aplica el umbral correcto por código HS.",
    "passport.steel_verification": "Fundido y Vaciado del Acero (Alisio)",
    "passport.lme_deviation": "Desviación Precio LME",
    "passport.steel_passport_note": "El pasaporte registra el proveedor de acero. Alisio verifica el origen de fundido y vaciado contra el Artículo 4.6 del USMCA y señala anomalías de precio LME.",
    "passport.sat_cross_ref": "Referencia Cruzada SAT (Alisio)",
    "passport.triple_source": "Verificación Triple Fuente",
    "passport.total_sub_suppliers": "Total Sub-Proveedores",
    "passport.blocked": "Bloqueados",
    "passport.sub_supplier_note": "El pasaporte rastrea materiales dentro de Katcon. Alisio extiende la verificación a los proveedores Tier 2 de Katcon — verificación RFC, revisión EFOS, verificación de certificado de molino.",
    "passport.eu_dpp_ready": "Exportación DPP UE Lista",
    "passport.eu_dpp_note": "Datos del pasaporte + verificación Alisio exportables al esquema de Pasaporte Digital de Producto de la UE. Listo para operaciones europeas de Katcon cuando el mandato DPP UE entre en vigor.",
    "passport.carbon_raw_material": "Materia Prima",
    "passport.carbon_forging": "Forja",
    "passport.carbon_machining": "Maquinado",
    "passport.carbon_heat_treatment": "Tratamiento Térmico",
    "passport.carbon_transport": "Transporte",

    // =========================================================================
    // BANK VIEW — DIVERSIFICATION
    // =========================================================================
    "bank.diversification_title": "Diversificación de Compradores",
    "bank.diversification_note": "Portafolio de tres compradores reduce riesgo de concentración. TREMEC en 62% (antes 78%). Términos de crédito mejoran con cuentas por cobrar diversificadas.",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — PAGE
    // =========================================================================
    "mdv.page_title": "Manifestación de Valor",
    "mdv.page_subtitle": "Nueva disposición aduanera mexicana — conciliación de valor en la cadena de suministro",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — SECTION A: RECONCILIATION ENGINE
    // =========================================================================
    "mdv.reconciliation_title": "A. Motor de Conciliación Automática",
    "mdv.reconciliation_subtitle": "Comparación tripartita: Valor Aduanero Declarado vs Factura Comercial vs Pago Real",
    "mdv.reconciliation_btn": "Ejecutar Conciliación",
    "mdv.reconciliation_running": "Conciliando...",
    "mdv.reconciliation_complete": "Conciliación Completa",
    "mdv.col_sub_supplier": "Sub-Proveedor",
    "mdv.col_customs_value": "Valor Aduanero",
    "mdv.col_invoice": "Factura",
    "mdv.col_payment": "Pago",
    "mdv.col_delta": "Delta",
    "mdv.col_status": "Estado",
    "mdv.status_flagged": "MARCADO",
    "mdv.status_clean": "APROBADO",
    "mdv.flagged_alert_title": "Fundición Industrial Celaya — RECHAZO MARCADO",
    "mdv.flagged_alert_detail": "Discrepancia del 16% detectada entre el valor aduanero declarado ($483,840) y la factura comercial ($561,254). Este sub-proveedor no puede incluirse en el Certificado de Origen T-MEC hasta que se resuelvan las discrepancias.",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — SECTION B: QUALITY GATE PORTAL
    // =========================================================================
    "mdv.quality_gate_title": "B. Portal de Control de Calidad de Sub-Proveedores",
    "mdv.quality_gate_subtitle": "Revisión individual de cumplimiento por sub-proveedor — origen del acero, VCR, conciliación de valor",
    "mdv.gate_flagged": "RECHAZO MARCADO",
    "mdv.gate_clean": "APROBADO",
    "mdv.steel_origin": "Origen del Acero",
    "mdv.mill_cert": "Cert. de Planta",
    "mdv.no_mill_cert": "Sin certificado de planta en archivo",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — SECTION C: T-MEC READINESS
    // =========================================================================
    "mdv.tmec_readiness_title": "C. Preparación T-MEC 2026",
    "mdv.tmec_readiness_subtitle": "Estado de documentación por sub-proveedor — desde datos hasta Certificado T-MEC",
    "mdv.audit_ready": "Listo para Auditoría",
    "mdv.blocked": "Bloqueado",
    "mdv.tmec_ready": "LISTO PARA AUDITORÍA",
    "mdv.tmec_blocked": "BLOQUEADO",
    "mdv.flow_sub_data": "Datos Sub-Proveedor",
    "mdv.flow_reconciliation": "Conciliación Alisio",
    "mdv.flow_mdv": "Manifestación de Valor",
    "mdv.flow_tmec_cert": "Certificado T-MEC",
    "mdv.detail_rvc": "VCR",
    "mdv.detail_steel": "Acero",
    "mdv.detail_mdv": "Docs MdV",
    "mdv.blocked_reason": "BLOQUEADO: Discrepancia de valor + origen de acero no verificado + VCR debajo del umbral. No se puede emitir Certificado T-MEC hasta que se resuelvan todas las alertas.",
    "mdv.tmec_summary": "2 de 3 sub-proveedores están listos para auditoría de certificación T-MEC 2026. Fundición Industrial Celaya está BLOQUEADO debido a discrepancia en valor aduanero, origen de acero no verificado, y VCR debajo del umbral de 75% para partes centrales. El motor de conciliación de Alisio detectó estos problemas automáticamente — antes de llegar a aduanas.",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — MVE RECONCILIATION ENGINE (Section A cards)
    // =========================================================================
    "mve.reconciliation.description": "Para cada transaccion, Alisio verifica el valor CFDI, la orden de compra y la confirmacion de pago en todos los sub-proveedores.",
    "mve.reconciliation.match": "COINCIDE",
    "mve.reconciliation.cannotReconcile": "NO SE PUEDE RECONCILIAR",
    "mve.gate.approved": "Lista para transmision",
    "mve.gate.blocked": "Bloqueada",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — MVE QUALITY GATE PORTAL (Section B split-screen)
    // =========================================================================
    "mve.left_column_header": "VISTA DE CARGA — SUB-PROVEEDOR",
    "mve.left_column_title": "VISTA DEL SUB-PROVEEDOR",
    "mve.right_column_header": "VALIDACIÓN TREMEC / ALISIO",
    "mve.right_column_title": "RESULTADO TREMEC / ALISIO",
    "mve.uploaded_cfdi_clean": "CFDI subido — Alisio validó automáticamente",
    "mve.uploaded_cfdi_flagged": "CFDI subido — Alisio DETECTÓ PROBLEMAS",
    "mve.cfdi_amount": "Monto CFDI",
    "mve.product": "Producto",
    "mve.processing_time": "Tiempo de procesamiento",
    "mve.no_password_no_erp": "Sin contraseña, sin ERP",
    "mve.timeline": "Línea de tiempo",
    "mve.timeline_upload": "Carga",
    "mve.timeline_validation": "Validación",
    "mve.timeline_result": "Resultado",
    "mve.auto_notification_sent": "Notificación automática enviada al proveedor",
    "mve.resultado_alisio": "RESULTADO ALISIO",
    "mve.checks_passed": "verificaciones aprobadas",
    "mve.critical_failures": "fallas críticas",
    "mve.alerta_origen": "ALERTA DE ORIGEN",
    "mve.check_hs_code": "Código HS",
    "mve.pending_review": "pendiente de revisión",
    "mve.verified": "verificado",
    "mve.check_price_lme": "Precio vs LME",
    "mve.within_range": "dentro del rango",
    "mve.below_range": "debajo del rango",
    "mve.check_origin": "Origen",
    "mve.unverifiable": "no verificable",
    "mve.check_reconciliation": "Reconciliación 3 vías",
    "mve.cannot_reconcile": "no se puede reconciliar",
    "mve.no_payment_proof": "sin comprobante de pago",
    "mve.batch_audit_ready": "Lote: 100% listo para auditoría",
    "mve.mve_ready": "MVE LISTA",
    "mve.datos_bloqueados": "DATOS BLOQUEADOS",
    "mve.blocked_cannot_proceed": "Esta transacción NO tocará la MVE de TREMEC hasta resolver",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — MVE READINESS (Section C rewrite)
    // =========================================================================
    // Section 1: IMMEX Program Health
    "mve.immex_health_title": "Salud del Programa IMMEX",
    "mve.status_label": "Estado",
    "mve.status_healthy": "SALUDABLE",
    "mve.months_no_discrepancy": "Meses sin discrepancia",
    "mve.audit_risk": "Riesgo de auditoría",
    "mve.audit_risk_low": "BAJO",
    "mve.key_indicators": "Indicadores Clave",
    "mve.sub_supplier_txns": "Transacciones de sub-proveedores este período",
    "mve.auto_approved": "Auto-aprobadas (Verde)",
    "mve.corrected_approved": "Corregidas y aprobadas (Amarillo)",
    "mve.blocked_red": "Bloqueadas (Rojo)",
    "mve.mve_zero_discrepancy": "MVE con cero discrepancias",
    "mve.avg_resolution_time": "Tiempo promedio de resolución",
    "mve.hours": "horas",

    // Section 2: MVE Verification Checklist
    "mve.verification_title": "Verificación MVE — Lista para Junio 2026",
    "mve.digital_infra": "Infraestructura Digital",
    "mve.vucem_access": "Acceso VUCEM",
    "mve.efirma_importer": "e.firma (importador)",
    "mve.e2_format": "Formato E2 probado",
    "mve.customs_broker": "Agente aduanal autorizado",
    "mve.docs_per_supplier": "Documentación por Sub-Proveedor",
    "mve.complete": "completo",
    "mve.docs_missing": "docs faltantes",
    "mve.regional_content": "Contenido Regional (VCR)",
    "mve.weighted_avg": "Promedio ponderado",
    "mve.threshold": "umbral",
    "mve.supplier_below": "proveedor debajo del umbral",
    "mve.trend_stable": "Tendencia: estable últimos 6 meses",

    // Section 3: IMMEX Risk Ladder
    "mve.risk_ladder_title": "Escalera de Riesgo IMMEX",
    "mve.without_alisio": "Sin Alisio",
    "mve.with_alisio": "Con Alisio",
    "mve.risk_step1_no": "Sub-proveedor entrega datos incorrectos",
    "mve.risk_step2_no": "TREMEC declara valor incorrecto en VUCEM",
    "mve.risk_step3_no": "SAT cruza información — detecta discrepancia",
    "mve.risk_step4_no": "Auditoría — Multas $29K-$107K MXN por operación",
    "mve.risk_step5_no": "Patrón de discrepancias — revisión del programa IMMEX",
    "mve.risk_step6_no": "REVOCACIÓN IMMEX — Aranceles completos en importaciones — TREMEC no puede competir",
    "mve.risk_step1_yes": "Sub-proveedor sube CFDI — Alisio valida en 30 seg",
    "mve.risk_step2_yes": "Datos limpios — MVE / Datos sucios — BLOQUEADO",
    "mve.risk_step3_yes": "100% de MVE enviadas con cero discrepancias",
    "mve.risk_step4_yes": "Auditoría — Sin hallazgos — Programa IMMEX seguro",

    // Generate Report Button
    "mve.generate_report": "Generar Reporte MVE",
    "mve.report_tooltip": "Genera un reporte completo de cumplimiento MVE para TREMEC — endpoint de demostración",

    // =========================================================================
    // MANIFESTACIÓN DE VALOR — MVE EXTENDED KEYS
    // =========================================================================
    "mve.title": "Manifestación de Valor Electrónica",
    "mve.reconciliation.title": "Motor de Reconciliación Automática",
    "mve.reconciliation.cfdi_value": "Valor CFDI",
    "mve.reconciliation.purchase_order": "Orden de Compra",
    "mve.reconciliation.payment_confirmed": "Pago Confirmado",
    "mve.reconciliation.payment_unconfirmed": "SIN CONFIRMAR",
    "mve.reconciliation.cannot_reconcile": "NO SE PUEDE RECONCILIAR",
    "mve.reconciliation.unit_price_vs_market": "Precio Unitario vs Mercado",
    "mve.reconciliation.origin_verified": "Origen Verificado",
    "mve.reconciliation.incoterm_check": "Verificación INCOTERM",
    "mve.reconciliation.range": "Rango",
    "mve.reconciliation.anomaly": "ANOMALÍA",
    "mve.reconciliation.consistent": "consistente",
    "mve.reconciliation.freight_matches": "Flete coincide",
    "mve.reconciliation.no_certificate": "Sin certificado",
    "mve.reconciliation.unverifiable": "No verificable",
    "mve.reconciliation.no_mill_cert": "Sin cert. de molino",
    "mve.reconciliation.freight": "Flete",
    "mve.reconciliation.gate_ready": "LISTA PARA TRANSMISION / READY FOR SUBMISSION",
    "mve.reconciliation.gate_blocked": "BLOQUEADA / BLOCKED",
    "mve.reconciliation.checks_passed": "verificaciones aprobadas — todas reconciliadas",
    "mve.reconciliation.critical_failures": "fallas criticas:",
    "mve.reconciliation.approved": "APROBADA",
    "mve.reconciliation.blocked": "BLOQUEADA",
    "mve.gate.approved_detail": "Todos los valores reconciliados. Documentación completa.",
    "mve.gate.blocked_detail": "fallas críticas. Esta transacción NO PUEDE incluirse en la Manifestación de Valor de TREMEC.",
    "mve.gate.pending": "Pendiente de corrección",
    "mve.gate.remediation_link": "Ver detalles de remediación",
    "mve.mve_status": "ESTADO MVE",
    "mve.qualityGate.title": "Control de Calidad de Sub-Proveedores",
    "mve.qualityGate.description": "Alisio valida los datos del sub-proveedor antes de que lleguen a la declaración de TREMEC",
    "mve.qualityGate.sub_supplier_view": "VISTA DEL SUB-PROVEEDOR",
    "mve.qualityGate.tremec_view": "VISTA DE TREMEC",
    "mve.qualityGate.uploaded_cfdi": "Subió CFDI → Alisio lo validó automáticamente",
    "mve.qualityGate.detected_issues": "Subió CFDI → Alisio DETECTÓ PROBLEMAS",
    "mve.qualityGate.processing_time": "Tiempo: 30 segundos",
    "mve.qualityGate.no_password": "Sin contraseña, sin ERP",
    "mve.qualityGate.notification_sent": "Alisio envió notificación automática al proveedor pidiendo:",
    "mve.qualityGate.alisio_result": "RESULTADO ALISIO",
    "mve.qualityGate.origin_alert": "ALERTA DE ORIGEN",
    "mve.qualityGate.anomaly_detected": "Anomalía de precio detectada. Posible material no originario fuera de zona USMCA.",
    "mve.qualityGate.data_blocked": "DATOS BLOQUEADOS",
    "mve.qualityGate.data_blocked_detail": "Esta transacción NO tocará la MVE de TREMEC hasta resolver",
    "mve.qualityGate.batch_ready": "Lote: 100% listo para auditoría",
    "mve.qualityGate.hs_verified": "Código HS verificado",
    "mve.qualityGate.price_in_range": "Precio dentro de rango LME",
    "mve.qualityGate.origin_check": "Origen:",
    "mve.qualityGate.rvc_check": "VCR:",
    "mve.qualityGate.threshold": "umbral:",
    "mve.qualityGate.reconciliation_ok": "Reconciliación 3 vías OK",
    "mve.qualityGate.price_below": "Precio: -41% vs mercado",
    "mve.qualityGate.no_mill_cert": "Sin certificado de molino",
    "mve.qualityGate.no_contract": "Sin contrato formal",
    "mve.readiness.title": "Preparación T-MEC 2026",
    "mve.readiness.program_title": "TREMEC — Programa de Sincronizadores",

    // =========================================================================
    // DECISION AUDIT DRAWER
    // =========================================================================
    "audit.what_decided": "Qué Se Decidió",
    "audit.how_calculated": "Cómo Se Calculó",
    "audit.data_used": "Datos Utilizados",
    "audit.rules_applied": "Reglas Aplicadas",
    "audit.human_oversight": "Supervisión Humana",
    "audit.document_chain": "Cadena Documental",
    "audit.confidence": "Confianza",
    "audit.reviewed_by": "Revisado por",
    "audit.pending_review": "Esperando revisión humana — decisión aún no finalizada",
    "audit.auto_approved": "Auto-aprobado — confianza sobre umbral, sin anomalías detectadas",
    "audit.decision_hash": "Hash de decisión",
    "audit.export_record": "Exportar Registro de Auditoría",

    // =========================================================================
    // MISSING HARDCODED STRINGS — ES equivalents
    // =========================================================================
    "common.new": "NUEVO",
    "common.rvc": "VCR",
    "common.compliant": "Cumple",
    "common.non_compliant": "No cumple",
    "sec.level_prefix": "Nivel",
    "sec.flow_verify": "Verificar",
    "sec.flow_clean": "Limpio",
    "sec.flow_package": "Paquete",
    "sec.flow_oem_bank": "OEM / Banco",
    "katcon.hs_code": "Código HS",

    // KATCON CORRECTION WORKFLOW
    "katcon.portal_title": "Portal de Cumplimiento Katcon",
    "katcon.portal_subtitle": "Cumplimiento de la cadena de proveedores para los programas de escape de Katcon.",
    "katcon.tab_overview": "Resumen",
    "katcon.tab_suppliers": "Proveedores",
    "katcon.tab_alerts": "Alertas",
    "katcon.hs_issues": "Problemas de Código HS",
    "katcon.mv_ready": "MV Listo",
    "katcon.s232_exposure": "Exposición Sección 232",
    "katcon.apply_correction": "Aplicar Corrección",
    "katcon.correction_title": "Corrección Código HS — Propagación en Vivo",
    "katcon.correction_subtitle": "Una corrección se propaga por toda la cadena de documentos.",
    "katcon.confirm_correction": "Confirmar Corrección",
    "katcon.correction_applied": "Corrección Aplicada",
    "katcon.close_return": "Cerrar y Volver al Portal",
    "katcon.propagating": "Propagando...",
    "katcon.correction_history": "Historial de Correcciones",
    "katcon.resolved": "Resuelto",

    "tc.instant": "Instantáneo",
    "bank.steel_chain_fmb": "FMB (forja+maquinado+TT)",
    "bank.steel_chain_tremec": "TREMEC (ensamble)",
    "bank.steel_label": "Acero",
    "recv.hero_batch_label": "Lote #4456 — TREMEC",
    "recv.hero_net_90": "NET 90",
    "recv.hero_badge": "PRINCIPAL",
    "recv.net_prefix": "NET",
    "mve.chain_stopped": "— cadena detenida —",
    "tremec.rvc_prefix": "VCR",
    "tremec.ia_badge": "IA",

    // =========================================================================
    // PORTAL SHARED KEYS (Katcon / Nexteer / TREMEC portals)
    // =========================================================================
    "portal.tab_overview": "Resumen",
    "portal.tab_suppliers": "Proveedores",
    "portal.tab_alerts": "Alertas",
    "portal.tab_option1": "Opción 1: Cumplimiento",
    "portal.tab_option2": "Opción 2: Pago Anticipado",
    "portal.tab_digital_passport": "Pasaporte Digital",
    "portal.supplier_compliance_portal": "Portal de Cumplimiento de Proveedores",
    "portal.reset_demo": "Reiniciar Demo",
    "portal.mv_deadline": "Fecha Límite MV: 1 de Junio",
    "portal.days": "días",

    // Katcon-specific portal keys
    "katcon.tab_customs": "Aduanas y 232",
    "katcon.tab_sap": "Integración SAP",
    "katcon.demo_banner": "Modo Demo — Mostrando datos representativos para la cadena de suministro de Katcon. Conecte su SAP para datos reales.",
    "katcon.edicom_title": "Complemento con EDICOM",

    // =========================================================================
    // TREMEC COMPLIANCE TABS (Overview, USMCA & RVC, Suppliers, Alerts)
    // =========================================================================
    "demo.tab_overview": "Resumen",
    "demo.tab_usmca": "USMCA y VCR",
    "demo.tab_suppliers_compliance": "Proveedores",
    "demo.tab_alerts": "Alertas",

    // Overview tab
    "tremec.overview_icts_exposure": "Exposición ICTS",
    "tremec.overview_icts_sub": "proveedor(es) con riesgo TCU/sensor",
    "tremec.overview_avg_rvc": "VCR Promedio Transmisiones",
    "tremec.overview_rvc_sub": "arriba del umbral de 75%",
    "tremec.overview_steel_verified": "Origen Acero Verificado",
    "tremec.overview_steel_sub": "Fusión y vaciado USMCA confirmado",
    "tremec.overview_mv_ready": "MV Listo",
    "tremec.overview_mv_sub": "Manifestación de Valor",
    "tremec.overview_table_title": "Resumen de Cumplimiento de Proveedores TREMEC",
    "tremec.overview_th_hts": "Código HTS",
    "tremec.overview_th_icts": "ICTS",
    "tremec.overview_th_risk": "Riesgo",
    "tremec.overview_th_efos": "EFOS",
    "tremec.overview_th_steel": "Acero",
    "tremec.overview_th_mv": "MV",

    // RFC Screening
    "tremec.rfc_title": "Verificación RFC / EFOS 69-B",
    "tremec.rfc_help": "Pegue uno o más códigos RFC para verificar contra la lista EFOS 69-B del SAT y la base de riesgos de Alisio. Pruebe: FMB850312AAA, CAC880715DDD",
    "tremec.rfc_placeholder": "Ingrese códigos RFC (uno por línea o separados por comas)...",
    "tremec.rfc_screening": "Verificando",
    "tremec.rfc_run": "Ejecutar Verificación",
    "tremec.rfc_download": "Descargar Resultados",
    "tremec.rfc_clean": "Limpio",
    "tremec.rfc_clean_demo": "Limpio (demo)",
    "tremec.rfc_unknown": "Desconocido",

    // USMCA & RVC tab
    "tremec.usmca_title": "Cumplimiento USMCA y VCR",
    "tremec.scenario_current": "Reglas Vigentes (2026)",
    "tremec.scenario_jr2026": "RJ 2026 — Propuesto",
    "tremec.scenario_2027": "2027 Acero + ICTS",
    "tremec.scenario_current_desc": "USMCA Capítulo 4 vigente. Partes principales: 75%. Roll-up activo. Acero: fusión y vaciado en país USMCA.",
    "tremec.scenario_jr2026_desc": "Principal 75%→80%. Roll-up eliminado. Divulgación de afiliación china (>25% propiedad). Contenido no originario se rastrea al nivel del vehículo.",
    "tremec.scenario_2027_desc": "Todos los cambios RJ 2026 + acero TODOS los procesos en USMCA (Art. 4.6) + prohibición hardware ICTS MY 2030. Transmisiones con TCU se convierten en hardware cubierto.",
    "tremec.rvc_dashboard": "Dashboard VCR TREMEC — Por Línea de Producto",
    "tremec.margin": "margen",
    "tremec.classification_title": "Clasificación de Productos TREMEC — USMCA Principal / Secundario / Complementario",
    "tremec.th_product_line": "Línea de Producto",
    "tremec.th_category": "Categoría",
    "tremec.th_threshold": "Umbral",
    "tremec.th_current_rvc": "VCR Actual",
    "tremec.th_status": "Estado",
    "tremec.th_notes": "Notas",
    "tremec.rollup_overturned": "Provisión de roll-up revocada.",
    "tremec.rollup_overturned_detail": "Bajo las reglas propuestas, el contenido no originario en subensambles de transmisión se rastrea hasta el nivel del vehículo. El roll-up a nivel de subensamble ya no protege el cálculo de VCR del vehículo final.",
    "tremec.rollup_traces": "Roll-up revocado: {pct}% contenido no originario se rastrea al nivel del vehículo",

    // RVC Calculator
    "tremec.calc_title": "Calculadora VCR — Método de Reducción",
    "tremec.calc_desc": "VCR = ((VT - MNO) / VT) × 100. Para transmisiones (partes principales), mínimo 75% bajo reglas vigentes.",
    "tremec.calc_tv": "Valor de Transacción (VT)",
    "tremec.calc_vnm": "Materiales No Originarios (MNO)",
    "tremec.calc_pass": "APROBADO",
    "tremec.calc_fail": "FALLO",
    "tremec.calc_above": "arriba del umbral",
    "tremec.calc_below": "debajo del umbral",

    // Steel Origin
    "tremec.steel_title": "Verificación de Origen de Acero y Aluminio",
    "tremec.steel_all_processes": "TODOS LOS PROCESOS REQUERIDOS",
    "tremec.th_material": "Material",
    "tremec.th_origin": "Origen",
    "tremec.th_mill_cert": "Cert. Molino",
    "tremec.th_s232_exposure": "Exposición §232",
    "tremec.th_melt_pour": "Fusión y Vaciado",
    "tremec.th_all_processes": "Todos los Procesos",
    "tremec.steel_verified": "Verificado",
    "tremec.steel_non_usmca": "No USMCA",
    "tremec.steel_art46_title": "Endurecimiento Artículo 4.6 (Jul 2027):",
    "tremec.steel_art46_detail": "El acero debe tener TODOS los pasos de procesamiento (fusión, vaciado, fundición, laminación en caliente, laminación en frío) realizados en un país USMCA. El estándar actual de \"fusión y vaciado\" ya no será suficiente. El acero de origen chino relaminado o reprocesado queda descalificado.",

    // Section 232
    "tremec.s232_title": "Exposición a Aranceles de Acero Sección 232",
    "tremec.s232_rate": "Tasa Actual",
    "tremec.s232_rate_desc": "sobre acero no USMCA",
    "tremec.s232_total": "Exposición Total",
    "tremec.s232_total_desc": "proveedores sin verificar",
    "tremec.s232_at_risk": "Proveedores en Riesgo",
    "tremec.s232_at_risk_desc": "certificados faltantes",

    // Suppliers tab
    "tremec.suppliers_title": "Detalle de Cumplimiento de Proveedores TREMEC",
    "tremec.icts_assessment": "Evaluación de Riesgo ICTS",
    "tremec.rvc_contribution": "Contribución VCR",
    "tremec.relocation_progress": "Progreso de Reubicación",
    "tremec.s232_exposure": "Exposición Arancelaria §232",
    "tremec.s232_tariff_desc": "50% arancel sobre acero no USMCA",
    "tremec.on_file": "En archivo",

    // Alerts tab
    "tremec.alerts_title": "Alertas de Cumplimiento TREMEC",
    "tremec.alerts_critical": "Crítico — Acción Inmediata Requerida",
    "tremec.alerts_warning": "Advertencias — Acción Recomendada",
    "tremec.alerts_info": "Informativo — Monitorear",
    "tremec.alert_critical": "Crítico",
    "tremec.alert_warning": "Advertencia",
    "tremec.alert_info": "Info",
  },
};
